import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft,
  ArrowUpDown,
  Settings, 
  Users, 
  Calendar, 
  CalendarClock,
  FileText, 
  Download, 
  Upload, 
  CheckCircle, 
  X, 
  Copy, 
  Check, 
  ExternalLinkIcon, 
  CreditCard,
  QrCode,
  Printer,
  Clock,
  Eye,
  BookOpen,
  Star,
  StarOff,
  TrendingUp,
  Heart,
  Share2,
  AlertCircle,
  Image as ImageIcon,
  User,
  MapPin,
  Globe,
  UserCircle,
  Award,
  Building,
  Grid3X3,
  Mail,
  Phone,
  DollarSign,
  Send,
  Smartphone,
  ShoppingCart,
  BarChart3,
  Trophy,
  Vote,
  Info,
  Image,
  Ticket,
  RefreshCw,
  UserCheck,
  Search,
  Filter,
  Edit,
  MoreVertical,
  ExternalLink,
  Bell,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';

// EXTRACTED COMPONENTS
import ArtworksTab from './CollectionDetailAdmin/components/ArtworksTab';
import ArtworkCard from './CollectionDetailAdmin/components/ArtworkCard';
import CompactArtworkCard from './CollectionDetailAdmin/components/CompactArtworkCard';
import QualityBadge from './CollectionDetailAdmin/components/QualityBadge';
import PurchasePanel from './CollectionDetailAdmin/components/PurchasePanel';
import SubmissionTab from './CollectionDetailAdmin/components/SubmissionTab';
import EventHeaderBanner from './CollectionDetailAdmin/components/EventHeaderBanner';
import CuratorToolsDropdown from './CollectionDetailAdmin/components/CuratorToolsDropdown';
import GetStartedWorkflow from './CollectionDetailAdmin/components/GetStartedWorkflow';
import JudgingTab from './CollectionDetailAdmin/components/JudgingTab';
import FinancialSummaryTab from './CollectionDetailAdmin/components/FinancialSummaryTab';
// Note: PrintCampaignTab moved to standalone page at /curator/collections/:id/prints
import { supabase } from '../utils/supabaseClient';
import EventAttendeeManagement from './EventAttendeeManagement';
import { EventbriteApiService, NotificationService, AttendeeUtils } from '../utils/eventApiHelpers';
import QRCode from 'qrcode';
import StoryCreationModal from './StoryCreationModal';
import { getCloudinaryUrl } from '../utils/imageUtils';
import '../styles/CollectionDetailAdmin.css';
import './CollectionDetailAdmin/components/EventHeaderBanner.css';
import './CollectionDetailAdmin/components/CuratorToolsDropdown.css';
import './CollectionDetailAdmin/components/GetStartedWorkflow.css';


// VotingAdminSummary Component
const VotingAdminSummary = ({ collectionId, setSelectedArtwork }) => {
  const [votingData, setVotingData] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPeriodId, setEditingPeriodId] = useState(null);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState(null);
  const defaultStart = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };
  const defaultEnd = () => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };
  const [createForm, setCreateForm] = useState({
    title: "People's Choice Awards",
    description: 'Vote for your top three favorite artworks. One ballot per day.',
    voting_start: defaultStart(),
    voting_end: defaultEnd(),
    allow_artwork_votes: true,
    allow_story_votes: false,
    mixed_content_voting: false,
    show_live_vote_counts: true,
    show_winning_status: true,
    show_vote_breakdown: false,
    show_total_voters: true,
    anonymous_voting: false,
  });

  const handleCreateVotingPeriod = async (e) => {
    e.preventDefault();
    try {
      setCreateSaving(true);
      setCreateError(null);

      const payload = {
        title: createForm.title,
        description: createForm.description,
        voting_start: new Date(createForm.voting_start).toISOString(),
        voting_end: new Date(createForm.voting_end).toISOString(),
        is_active: true,
        allow_artwork_votes: createForm.allow_artwork_votes,
        allow_story_votes: createForm.allow_story_votes,
        mixed_content_voting: createForm.mixed_content_voting,
        show_live_vote_counts: createForm.show_live_vote_counts,
        show_winning_status: createForm.show_winning_status,
        show_vote_breakdown: createForm.show_vote_breakdown,
        show_total_voters: createForm.show_total_voters,
        anonymous_voting: createForm.anonymous_voting,
      };

      if (editingPeriodId) {
        // Update existing voting period
        const { error: updateErr } = await supabase
          .from('collection_voting_periods')
          .update(payload)
          .eq('voting_period_id', editingPeriodId);
        if (updateErr) throw updateErr;
      } else {
        // Find the event linked to this collection
        const { data: eventRow, error: eventErr } = await supabase
          .from('events')
          .select('id')
          .eq('collection_id', collectionId)
          .order('id', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (eventErr) throw eventErr;
        if (!eventRow) throw new Error('No event found for this collection. Create an event first.');

        const { error: insertErr } = await supabase
          .from('collection_voting_periods')
          .insert({ ...payload, collection_id: collectionId, event_id: eventRow.id });
        if (insertErr) throw insertErr;
      }

      setShowCreateForm(false);
      setEditingPeriodId(null);
      await loadVotingData();
    } catch (err) {
      console.error('Error saving voting period:', err);
      setCreateError(err.message);
    } finally {
      setCreateSaving(false);
    }
  };

  const handleEditVotingPeriod = () => {
    const vp = votingData?.voting_period;
    if (!vp) return;
    const toLocalInput = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
      return d.toISOString().slice(0, 16);
    };
    setCreateForm({
      title: vp.title || '',
      description: vp.description || '',
      voting_start: toLocalInput(vp.voting_start),
      voting_end: toLocalInput(vp.voting_end),
      allow_artwork_votes: vp.allow_artwork_votes ?? true,
      allow_story_votes: vp.allow_story_votes ?? false,
      mixed_content_voting: vp.mixed_content_voting ?? false,
      show_live_vote_counts: vp.show_live_vote_counts ?? true,
      show_winning_status: vp.show_winning_status ?? true,
      show_vote_breakdown: vp.show_vote_breakdown ?? false,
      show_total_voters: vp.show_total_voters ?? true,
      anonymous_voting: vp.anonymous_voting ?? false,
    });
    setEditingPeriodId(vp.voting_period_id);
    setShowCreateForm(true);
    setCreateError(null);
  };

  const renderCreateForm = () => (
    <form className="voting-create-form" onSubmit={handleCreateVotingPeriod}>
      <h4>{editingPeriodId ? 'Edit Voting Period' : 'Create Voting Period'}</h4>
      {createError && <div className="voting-error-inline">{createError}</div>}
      <label>
        Title
        <input
          type="text"
          value={createForm.title}
          onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
          required
        />
      </label>
      <label>
        Description
        <textarea
          value={createForm.description}
          onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
          rows={2}
        />
      </label>
      <div className="voting-form-row">
        <label>
          Voting start
          <input
            type="datetime-local"
            value={createForm.voting_start}
            onChange={(e) => setCreateForm({ ...createForm, voting_start: e.target.value })}
            required
          />
        </label>
        <label>
          Voting end
          <input
            type="datetime-local"
            value={createForm.voting_end}
            onChange={(e) => setCreateForm({ ...createForm, voting_end: e.target.value })}
            required
          />
        </label>
      </div>
      <div className="voting-form-checkboxes">
        <label>
          <input
            type="checkbox"
            checked={createForm.allow_artwork_votes}
            onChange={(e) => setCreateForm({ ...createForm, allow_artwork_votes: e.target.checked })}
          />
          Allow artwork votes
        </label>
        <label>
          <input
            type="checkbox"
            checked={createForm.allow_story_votes}
            onChange={(e) => setCreateForm({ ...createForm, allow_story_votes: e.target.checked })}
          />
          Allow story votes
        </label>
        <label>
          <input
            type="checkbox"
            checked={createForm.mixed_content_voting}
            onChange={(e) => setCreateForm({ ...createForm, mixed_content_voting: e.target.checked })}
          />
          Mixed content voting
        </label>
        <label>
          <input
            type="checkbox"
            checked={createForm.show_live_vote_counts}
            onChange={(e) => setCreateForm({ ...createForm, show_live_vote_counts: e.target.checked })}
          />
          Show live vote counts
        </label>
        <label>
          <input
            type="checkbox"
            checked={createForm.show_winning_status}
            onChange={(e) => setCreateForm({ ...createForm, show_winning_status: e.target.checked })}
          />
          Show winning status
        </label>
        <label>
          <input
            type="checkbox"
            checked={createForm.show_vote_breakdown}
            onChange={(e) => setCreateForm({ ...createForm, show_vote_breakdown: e.target.checked })}
          />
          Show vote breakdown
        </label>
        <label>
          <input
            type="checkbox"
            checked={createForm.show_total_voters}
            onChange={(e) => setCreateForm({ ...createForm, show_total_voters: e.target.checked })}
          />
          Show total voters
        </label>
        <label>
          <input
            type="checkbox"
            checked={createForm.anonymous_voting}
            onChange={(e) => setCreateForm({ ...createForm, anonymous_voting: e.target.checked })}
          />
          Anonymous voting
        </label>
      </div>
      <div className="voting-form-actions">
        <button type="button" className="btn-secondary" onClick={() => { setShowCreateForm(false); setEditingPeriodId(null); }} disabled={createSaving}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={createSaving}>
          {createSaving
            ? (editingPeriodId ? 'Saving…' : 'Creating…')
            : (editingPeriodId ? 'Save Changes' : 'Create Voting Period')}
        </button>
      </div>
    </form>
  );

  // Load voting data
  useEffect(() => {
    loadVotingData();
  }, [collectionId]);

  const loadVotingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Direct SQL call using Supabase RPC
      const { data, error: rpcError } = await supabase.rpc('get_collection_voting_admin_summary', {
        collection_id_param: collectionId
      });

      if (rpcError) throw rpcError;

      setVotingData(data);

      // Look up event id for the live voting link
      const { data: eventRow } = await supabase
        .from('events')
        .select('id')
        .eq('collection_id', collectionId)
        .order('id', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (eventRow?.id) setEventId(eventRow.id);
    } catch (err) {
      console.error('Error loading voting data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Export voting data
  const exportVotingData = async () => {
    try {
      if (!votingData) return;

      // Create CSV data
      const csvData = [];
      
      // Add header
      csvData.push(['Type', 'Rank', 'Title', 'Artist', 'First Place Votes', 'Second Place Votes', 'Third Place Votes', 'Total Points']);
      
      // Add artwork data
      if (votingData.leaderboards?.top_artworks) {
        votingData.leaderboards.top_artworks.forEach((artwork, index) => {
          csvData.push([
            'Artwork',
            index + 1,
            artwork.title,
            artwork.artist_name,
            artwork.first_place_votes,
            artwork.second_place_votes,
            artwork.third_place_votes,
            artwork.total_points
          ]);
        });
      }

      // Add story data
      if (votingData.leaderboards?.top_stories) {
        votingData.leaderboards.top_stories.forEach((story, index) => {
          csvData.push([
            'Story',
            index + 1,
            story.title,
            story.artist_name,
            story.first_place_votes,
            story.second_place_votes,
            story.third_place_votes,
            story.total_points
          ]);
        });
      }

      // Convert to CSV string
      const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `collection-${collectionId}-voting-results.csv`;
      link.click();
    } catch (err) {
      console.error('Error exporting data:', err);
    }
  };

  // Format time remaining
  const formatTimeRemaining = (hours) => {
    if (hours <= 0) return 'Voting ended';
    if (hours < 24) return `${Math.floor(hours)}h remaining`;
    const days = Math.floor(hours / 24);
    const remainingHours = Math.floor(hours % 24);
    return `${days}d ${remainingHours}h remaining`;
  };

  if (loading) {
    return (
      <div className="voting-admin-summary">
        <div className="voting-loading">
          <div className="spinner"></div>
          <p>Loading voting data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="voting-admin-summary">
        <div className="voting-error">
          <h4>Error Loading Voting Data</h4>
          <p>{error}</p>
          <button onClick={loadVotingData} className="btn-secondary">Retry</button>
        </div>
      </div>
    );
  }

  if (!votingData?.has_voting) {
    return (
      <div className="voting-admin-summary">
        {showCreateForm ? (
          renderCreateForm()
        ) : (
          <div className="no-voting">
            <Vote className="no-voting-icon" />
            <h4>No Voting Configured</h4>
            <p>This collection doesn't have an active voting period.</p>
            <button className="btn-primary" onClick={() => setShowCreateForm(true)}>
              Create Voting Period
            </button>
          </div>
        )}
      </div>
    );
  }

  const { voting_period, voting_statistics, leaderboards, recent_activity } = votingData;
  const liveVotingUrl = eventId ? `https://vibrantcast.com/collection-voting?id=${eventId}` : null;

  if (showCreateForm) {
    return <div className="voting-admin-summary">{renderCreateForm()}</div>;
  }

  return (
    <div className="voting-admin-summary">
      {/* Header */}
      <div className="voting-header">
        <div className="voting-title">
          <Vote className="voting-icon" />
          <div>
            <h3>Voting</h3>
            <p>{voting_period.title}</p>
          </div>
        </div>
        <div className="voting-actions">
          {liveVotingUrl && (
            <a
              href={liveVotingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', textDecoration: 'none' }}
              title="Open the public voting page in a new tab"
            >
              <ExternalLink size={16} />
              Live Voting Page
            </a>
          )}
          <button onClick={handleEditVotingPeriod} className="btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            <Edit size={16} />
            Edit
          </button>
          <button onClick={exportVotingData} className="btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            <Download size={16} />
            Export Results
          </button>
          <button onClick={loadVotingData} className="btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>
      {liveVotingUrl && (
        <div style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb', wordBreak: 'break-all' }}>
          Live URL: <a href={liveVotingUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#7c3aed' }}>{liveVotingUrl}</a>
          {' '}· Local test:{' '}
          <code style={{ background: '#f3f4f6', padding: '0.1rem 0.35rem', borderRadius: 4 }}>http://localhost:&lt;port&gt;/collection-voting?id={eventId}</code>
        </div>
      )}

      {/* Status Card */}
      <div className="voting-status-card">
        <div className="voting-status-content">
          <div className="status-info">
            <div className={`status-badge ${voting_period.is_currently_active ? 'active' : 'inactive'}`}>
              {voting_period.is_currently_active ? 'VOTING ACTIVE' : 'VOTING ENDED'}
            </div>
            <div className="time-remaining">
              <Clock className="time-icon" />
              {formatTimeRemaining(voting_period.hours_remaining)}
            </div>
          </div>
          <div className="quick-stats">
            <div className="quick-stat">
              <span className="stat-value">{voting_statistics.total_unique_voters}</span>
              <span className="stat-label">Voters</span>
            </div>
            <div className="quick-stat">
              <span className="stat-value">{voting_statistics.total_votes_cast}</span>
              <span className="stat-label">Total Votes</span>
            </div>
            <div className="quick-stat">
              <span className="stat-value">{voting_statistics.votes_today}</span>
              <span className="stat-label">Today</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="voting-tabs">
        <button 
          onClick={() => setActiveTab('leaderboard')}
          className={`tab-button ${activeTab === 'leaderboard' ? 'active' : ''}`}
        >
          <Trophy className="tab-icon" />
          Leaderboard
        </button>
        <button 
          onClick={() => setActiveTab('activity')}
          className={`tab-button ${activeTab === 'activity' ? 'active' : ''}`}
        >
          <TrendingUp className="tab-icon" />
          Recent Activity
        </button>
      </div>

      {/* Tab Content */}
      <div className="voting-tab-content">
        {activeTab === 'leaderboard' && (
          <div className="voting-leaderboard">
            {/* Top Artworks */}
            {leaderboards.top_artworks && leaderboards.top_artworks.length > 0 && (
              <div className="leaderboard-section">
                <h4>🎨 Top Artworks</h4>
                <div className="leaderboard-list">
                  {leaderboards.top_artworks.slice(0, 10).map((artwork, index) => (
                    <div key={`leaderboard-artwork-${artwork.vc_id}-${index}`} className={`leaderboard-item ${index < 3 ? 'top-three' : ''}`}>
                      <div className="rank-badge">
                        {index === 0 && '🥇'}
                        {index === 1 && '🥈'}
                        {index === 2 && '🥉'}
                        {index > 2 && `#${index + 1}`}
                      </div>
                      <div className="item-thumbnail">
                        {artwork.cover_image ? (
                          <img 
                            src={artwork.cover_image} 
                            alt={artwork.title}
                            className="artwork-thumbnail clickable-thumbnail"
                            onClick={() => setSelectedArtwork(artwork)}
                            title="Click to view artwork details"
                          />
                        ) : (
                          <div className="artwork-thumbnail-placeholder">
                            <ImageIcon className="placeholder-icon" />
                          </div>
                        )}
                      </div>
                      <div className="item-info">
                        <div className="item-title">{artwork.title}</div>
                        <div className="item-subtitle">by {artwork.artist_name}</div>
                      </div>
                      <div className="vote-breakdown">
                        <span className="vote-detail">🥇 {artwork.first_place_votes}</span>
                        <span className="vote-detail">🥈 {artwork.second_place_votes}</span>
                        <span className="vote-detail">🥉 {artwork.third_place_votes}</span>
                      </div>
                      <div className="total-points">{artwork.total_points} pts</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Stories */}
            {leaderboards.top_stories && leaderboards.top_stories.length > 0 && (
              <div className="leaderboard-section">
                <h4>📖 Top Stories</h4>
                <div className="leaderboard-list">
                  {leaderboards.top_stories.slice(0, 10).map((story, index) => (
                    <div key={story.story_id} className={`leaderboard-item ${index < 3 ? 'top-three' : ''}`}>
                      <div className="rank-badge">
                        {index === 0 && '🥇'}
                        {index === 1 && '🥈'}
                        {index === 2 && '🥉'}
                        {index > 2 && `#${index + 1}`}
                      </div>
                      <div className="item-thumbnail">
                        {story.cover_image ? (
                          <img 
                            src={story.cover_image} 
                            alt={story.title}
                            className="artwork-thumbnail clickable-thumbnail"
                            onClick={() => setSelectedArtwork(story)}
                            title="Click to view story details"
                          />
                        ) : (
                          <div className="artwork-thumbnail-placeholder">
                            <FileText className="placeholder-icon" />
                          </div>
                        )}
                      </div>
                      <div className="item-info">
                        <div className="item-title">{story.title}</div>
                        <div className="item-subtitle">by {story.artist_name} • {story.story_type}</div>
                      </div>
                      <div className="vote-breakdown">
                        <span className="vote-detail">🥇 {story.first_place_votes}</span>
                        <span className="vote-detail">🥈 {story.second_place_votes}</span>
                        <span className="vote-detail">🥉 {story.third_place_votes}</span>
                      </div>
                      <div className="total-points">{story.total_points} pts</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="voting-activity">
            {/* Recent Votes */}
            {recent_activity.recent_votes && recent_activity.recent_votes.length > 0 && (
              <div className="recent-votes-section">
                <h4>Recent Votes</h4>
                <div className="recent-votes-list">
                  {recent_activity.recent_votes.slice(0, 20).map((vote) => (
                    <div key={vote.vote_id} className="recent-vote-item">
                      <div className="voter-info">
                        <div className="voter-name">{vote.user_nickname || vote.user_handle}</div>
                        <div className="vote-time">{new Date(vote.created_at).toLocaleString()}</div>
                      </div>
                      <div className="vote-choices">
                        <div className="choice">
                          <span className="choice-rank">🥇</span>
                          <div className="choice-thumbnail">
                            {vote.first_place?.cover_image ? (
                              <img 
                                src={vote.first_place.cover_image} 
                                alt={vote.first_place.title}
                                className="choice-artwork-thumbnail clickable-thumbnail"
                                onClick={() => setSelectedArtwork(vote.first_place)}
                                title="Click to view details"
                              />
                            ) : (
                              <div className="choice-thumbnail-placeholder">
                                <ImageIcon className="placeholder-icon" />
                              </div>
                            )}
                          </div>
                          <div className="choice-details">
                            <span className="choice-title">{vote.first_place?.title || 'N/A'}</span>
                            <span className="choice-type">({vote.first_place?.type})</span>
                          </div>
                        </div>
                        <div className="choice">
                          <span className="choice-rank">🥈</span>
                          <div className="choice-thumbnail">
                            {vote.second_place?.cover_image ? (
                              <img 
                                src={vote.second_place.cover_image} 
                                alt={vote.second_place.title}
                                className="choice-artwork-thumbnail clickable-thumbnail"
                                onClick={() => setSelectedArtwork(vote.second_place)}
                                title="Click to view details"
                              />
                            ) : (
                              <div className="choice-thumbnail-placeholder">
                                <ImageIcon className="placeholder-icon" />
                              </div>
                            )}
                          </div>
                          <div className="choice-details">
                            <span className="choice-title">{vote.second_place?.title || 'N/A'}</span>
                            <span className="choice-type">({vote.second_place?.type})</span>
                          </div>
                        </div>
                        <div className="choice">
                          <span className="choice-rank">🥉</span>
                          <div className="choice-thumbnail">
                            {vote.third_place?.cover_image ? (
                              <img 
                                src={vote.third_place.cover_image} 
                                alt={vote.third_place.title}
                                className="choice-artwork-thumbnail clickable-thumbnail"
                                onClick={() => setSelectedArtwork(vote.third_place)}
                                title="Click to view details"
                              />
                            ) : (
                              <div className="choice-thumbnail-placeholder">
                                <ImageIcon className="placeholder-icon" />
                              </div>
                            )}
                          </div>
                          <div className="choice-details">
                            <span className="choice-title">{vote.third_place?.title || 'N/A'}</span>
                            <span className="choice-type">({vote.third_place?.type})</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Acceptances Panel Component - Shows artwork acceptance status for this collection
const ACCEPTANCES_PER_PAGE = 20;

const AcceptancesPanel = ({ artworks, toggleAcceptanceStatus, onSelectArtwork }) => {
  const [filter, setFilter] = useState('all'); // 'all', 'accepted', 'not_accepted'
  const [search, setSearch] = useState('');
  const [displayCount, setDisplayCount] = useState(ACCEPTANCES_PER_PAGE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loaderRef = useRef(null);

  // Filter artworks based on current filter
  const filteredArtworks = artworks.filter(artwork => {
    // Apply acceptance filter
    if (filter === 'accepted' && artwork.accepted !== true) return false;
    if (filter === 'not_accepted' && artwork.accepted !== false) return false;
    
    // Apply search filter
    if (search.trim()) {
      const s = search.toLowerCase();
      const title = (artwork.title || '').toLowerCase();
      const artistName = artwork.artist 
        ? (artwork.artist.nickname || `${artwork.artist.first_name || ''} ${artwork.artist.last_name || ''}`).toLowerCase()
        : '';
      if (!title.includes(s) && !artistName.includes(s)) return false;
    }
    
    return true;
  });

  // Paginated artworks for display
  const displayedArtworks = filteredArtworks.slice(0, displayCount);
  const hasMore = displayCount < filteredArtworks.length;

  // Reset display count when filters/search change
  useEffect(() => {
    setDisplayCount(ACCEPTANCES_PER_PAGE);
  }, [filter, search]);

  // Load more artworks
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    
    setIsLoadingMore(true);
    setTimeout(() => {
      setDisplayCount(prev => Math.min(prev + ACCEPTANCES_PER_PAGE, filteredArtworks.length));
      setIsLoadingMore(false);
    }, 300);
  }, [isLoadingMore, hasMore, filteredArtworks.length]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [loadMore, hasMore, isLoadingMore]);

  const acceptedCount = artworks.filter(a => a.accepted === true).length;
  const notAcceptedCount = artworks.filter(a => a.accepted === false).length;
  const pendingCount = artworks.filter(a => a.accepted === null || a.accepted === undefined).length;

  const getArtistName = (artwork) => {
    if (!artwork.artist) return 'Unknown Artist';
    return artwork.artist.nickname || 
           `${artwork.artist.first_name || ''} ${artwork.artist.last_name || ''}`.trim() || 
           'Unknown Artist';
  };

  return (
    <div className="acceptances-panel">
      <div className="panel-header">
        <h3>Artwork Acceptance Status</h3>
        <p>Review and manage artwork acceptance for this collection</p>
      </div>

      {/* Summary Stats */}
      <div className="acceptances-summary">
        <div className="summary-stat" onClick={() => setFilter('all')} style={{ cursor: 'pointer' }}>
          <span className="stat-value">{artworks.length}</span>
          <span className="stat-label">Total Artworks</span>
        </div>
        <div className="summary-stat accepted" onClick={() => setFilter('accepted')} style={{ cursor: 'pointer' }}>
          <span className="stat-value" style={{ color: '#16a34a' }}>{acceptedCount}</span>
          <span className="stat-label">Accepted</span>
        </div>
        <div className="summary-stat" onClick={() => setFilter('not_accepted')} style={{ cursor: 'pointer' }}>
          <span className="stat-value" style={{ color: '#d97706' }}>{notAcceptedCount}</span>
          <span className="stat-label">Not Accepted</span>
        </div>
        {pendingCount > 0 && (
          <div className="summary-stat">
            <span className="stat-value" style={{ color: '#6b7280' }}>{pendingCount}</span>
            <span className="stat-label">Pending Review</span>
          </div>
        )}
      </div>

      {/* Filter and Search Controls */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            fontSize: '0.875rem'
          }}
        >
          <option value="all">All Artworks ({artworks.length})</option>
          <option value="accepted">Accepted ({acceptedCount})</option>
          <option value="not_accepted">Not Accepted ({notAcceptedCount})</option>
        </select>
        
        <input
          type="text"
          placeholder="Search by title or artist..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: '200px',
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            fontSize: '0.875rem'
          }}
        />
      </div>

      {filteredArtworks.length === 0 ? (
        <div className="no-acceptances">
          <CheckCircle className="no-acceptances-icon" />
          <h4>No Artworks Found</h4>
          <p>No artworks match the current filter criteria.</p>
        </div>
      ) : (
        <>
          <div className="acceptances-table">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>Image</th>
                  <th>Title</th>
                  <th>Artist</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {displayedArtworks.map((artwork) => (
                  <tr key={artwork.vc_id}>
                    <td>
                      <div style={{ 
                        width: '50px', 
                        height: '50px', 
                        borderRadius: '6px',
                        overflow: 'hidden',
                        backgroundColor: '#f3f4f6'
                      }}>
                        <img 
                          src={getCloudinaryUrl(artwork.cover_image, { width: 100, height: 100 })}
                          alt={artwork.title}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover', 
                            cursor: 'pointer'
                          }}
                          onClick={() => onSelectArtwork && onSelectArtwork(artwork)}
                        />
                      </div>
                    </td>
                    <td>
                      <span 
                        style={{ cursor: 'pointer', color: '#6366f1', fontWeight: '500' }}
                        onClick={() => onSelectArtwork && onSelectArtwork(artwork)}
                      >
                        {artwork.title || 'Untitled'}
                      </span>
                    </td>
                    <td>{getArtistName(artwork)}</td>
                    <td>
                      <span 
                        className={`status-badge ${artwork.accepted === true ? 'accepted' : artwork.accepted === false ? 'rejected' : 'pending'}`}
                        style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          backgroundColor: artwork.accepted === true ? '#dcfce7' : artwork.accepted === false ? '#fef3c7' : '#f3f4f6',
                          color: artwork.accepted === true ? '#166534' : artwork.accepted === false ? '#92400e' : '#6b7280'
                        }}
                      >
                        {artwork.accepted === true ? '✓ Accepted' : artwork.accepted === false ? '○ Not Accepted' : '? Pending'}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => toggleAcceptanceStatus && toggleAcceptanceStatus(artwork)}
                        style={{
                          padding: '0.375rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          backgroundColor: artwork.accepted === true ? '#fef3c7' : '#dcfce7',
                          color: artwork.accepted === true ? '#92400e' : '#166534',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        {artwork.accepted === true ? 'Remove' : 'Accept'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Infinite scroll loader */}
          {hasMore && (
            <div
              ref={loaderRef}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '2rem',
                gap: '0.5rem'
              }}
            >
              {isLoadingMore && (
                <>
                  <div style={{ width: '24px', height: '24px', border: '3px solid #e5e7eb', borderTopColor: '#8b5cf6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading more...</span>
                </>
              )}
            </div>
          )}
          
          {/* Show count indicator */}
          <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.5rem' }}>
            Showing {displayedArtworks.length} of {filteredArtworks.length} artworks
          </div>
        </>
      )}
    </div>
  );
};

// Communication Panel Component
const CommunicationPanel = ({ collection, artworks, artists, acceptances }) => {
  const [selectedCommunication, setSelectedCommunication] = useState('');
  const [targetAudience, setTargetAudience] = useState('all_artists');
  const [customMessage, setCustomMessage] = useState('');
  const [testMode, setTestMode] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [eventDetails, setEventDetails] = useState({
    event_date: '',
    event_location: '',
    setup_time: ''
  });

  // Calculate audience statistics
  const audienceStats = {
    all_artists: artists.length,
    accepted_artists: artists.filter(artist => 
      artworks.some(artwork => artwork.artist?.uuid === artist.uuid && artwork.accepted)
    ).length,
    unpaid_artists: artists.filter(artist => !artist.has_paid).length,
    no_artwork_submitted: artists.filter(artist => artist.has_paid && !artist.has_submitted_artwork).length,
    needs_terms: artists.filter(artist => {
      // Artist needs terms if they have accepted artwork AND terms_accepted is empty object
      const hasAcceptedArtwork = artworks.some(artwork => artwork.artist?.uuid === artist.uuid && artwork.accepted);
      if (!hasAcceptedArtwork) return false;
      
      const acceptanceRecord = acceptances?.find(acc => acc.user_id === artist.uuid);
      if (!acceptanceRecord) return false;
      
      // Check if terms_accepted is empty object
      const termsAccepted = acceptanceRecord.terms_accepted || {};
      const needsTerms = Object.keys(termsAccepted).length === 0;
      
      return needsTerms;
    }).length
  };
  
  console.log('Audience stats:', audienceStats);
  console.log('Total artists:', artists.length);
  console.log('Total acceptances:', acceptances?.length);

  // Create a mapping for better labels
  const audienceLabels = {
    all_artists: 'All Artists',
    accepted_artists: 'Accepted Artworks',
    unpaid_artists: 'Unpaid Artists', 
    no_artwork_submitted: 'No Artwork Submitted',
    needs_terms: 'Needs Terms'
  };

  const communicationOptions = [
    {
      value: 'submission_reminder',
      label: 'Submission Reminder',
      description: 'Remind paid artists to submit their artwork',
      audience: 'no_artwork_submitted',
      email_template: 'submission_reminder',
      push: {
        title: 'Submit Your Artwork ⏰',
        message: 'Don\'t forget to submit for {collection_name} - deadline approaching!',
        category: 'collection_deadline'
      }
    },
    {
      value: 'deadline_warning', 
      label: 'Urgent Deadline Warning',
      description: 'Final warning before submission deadline',
      audience: 'no_artwork_submitted',
      email_template: 'deadline_urgent',
      push: {
        title: 'URGENT: Deadline Soon! ⚡',
        message: 'Submit for {collection_name} - deadline in 24 hours!',
        category: 'collection_urgent'
      }
    },
    {
      value: 'artwork_accepted',
      label: 'Acceptance Notification', 
      description: 'Congratulate artists on accepted artwork',
      audience: 'accepted_artists',
      email_template: 'artwork_acceptance',
      push: {
        title: 'Congratulations! 🎉',
        message: 'Your artwork was accepted for {collection_name}!',
        category: 'collection_acceptance'
      }
    },
    {
      value: 'event_announcement',
      label: 'Event Details',
      description: 'Share event logistics and details',
      audience: 'accepted_artists',
      email_template: 'event_details',
      push: {
        title: 'Event Details Available 📅',
        message: '{collection_name} opening soon - check your email!',
        category: 'event_notification'
      }
    },
    {
      value: 'needs_terms',
      label: 'Needs Terms',
      description: 'Remind artists to accept terms and conditions',
      audience: 'needs_terms',
      email_template: 'needs_terms',
      push: {
        title: 'Action Required: Accept Terms 📋',
        message: 'Please accept the terms for {collection_name}',
        category: 'collection_terms'
      }
    },
    {
      value: 'general_update',
      label: 'General Update',
      description: 'Custom message to selected audience',
      audience: 'all_artists',
      email_template: 'general_update',
      push: {
        title: 'Update: {collection_name}',
        message: 'New update about {collection_name}',
        category: 'collection_update'
      }
    }
  ];

  // Auto-select audience when communication type changes
  useEffect(() => {
    const selectedOption = communicationOptions.find(opt => opt.value === selectedCommunication);
    if (selectedOption) {
      setTargetAudience(selectedOption.audience);
    }
  }, [selectedCommunication]);

  // Send communication function
  const sendCommunication = async () => {
    if (!selectedCommunication) {
      alert('Please select a communication type');
      return;
    }

    setIsLoading(true);
    setResults(null);

    try {
      const selectedOption = communicationOptions.find(opt => opt.value === selectedCommunication);
      
      const payload = {
        collection_id: collection.collection_id,
        communication_type: selectedCommunication,
        target_audience: targetAudience,
        email_template: selectedOption.email_template,
        push_notification: selectedOption.push,
        test_mode: testMode,
        custom_message: customMessage,
        event_details: selectedCommunication === 'event_announcement' ? eventDetails : undefined,
        admin_user_id: null // Will work for now, or get real admin UUID from auth
      };

      console.log('Sending communication:', payload);

      const response = await fetch('https://api.vibrantcast.com/functions/v1/collection_communication_hub', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      setResults(result);

      if (result.success) {
        console.log('Communication sent successfully:', result);
      } else {
        console.error('Communication failed:', result);
      }

    } catch (error) {
      console.error('Error sending communication:', error);
      setResults({
        success: false,
        error: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Preview audience function
  const previewAudience = () => {
    switch (targetAudience) {
      case 'accepted_artists':
        return artists.filter(artist => 
          artworks.some(artwork => artwork.artist?.uuid === artist.uuid && artwork.accepted)
        );
      case 'unpaid_artists':
        return artists.filter(artist => !artist.has_paid);
      case 'no_artwork_submitted':
        return artists.filter(artist => artist.has_paid && !artist.has_submitted_artwork);
      case 'needs_terms':
        return artists.filter(artist => {
          const hasAcceptedArtwork = artworks.some(artwork => artwork.artist?.uuid === artist.uuid && artwork.accepted);
          if (!hasAcceptedArtwork) return false;
          
          const acceptanceRecord = acceptances?.find(acc => acc.user_id === artist.uuid);
          if (!acceptanceRecord) return false;
          
          const termsAccepted = acceptanceRecord.terms_accepted || {};
          return Object.keys(termsAccepted).length === 0;
        });
      case 'all_artists':
      default:
        return artists;
    }
  };

  const currentAudience = previewAudience();

  return (
    <div className="communication-panel" style={{ backgroundColor: 'white', borderRadius: '0.75rem', padding: '1.5rem', marginTop: '1.5rem', border: '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Send style={{ height: '1.25rem', width: '1.25rem', color: '#7c3aed' }} />
          Send Communications
        </h3>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Test Mode</label>
          <input
            type="checkbox"
            checked={testMode}
            onChange={(e) => setTestMode(e.target.checked)}
            style={{ height: '1rem', width: '1rem' }}
          />
          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            {testMode ? '(Admin emails only)' : '(Live mode)'}
          </span>
        </div>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.75rem' }}>
          Communication Type
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
          {communicationOptions.map((option) => {
            const count = audienceStats[option.audience] || 0;
            
            return (
              <div
                key={option.value}
                style={{
                  cursor: 'pointer',
                  border: selectedCommunication === option.value ? '2px solid #7c3aed' : '2px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  backgroundColor: selectedCommunication === option.value ? '#faf5ff' : 'white',
                  transition: 'all 0.2s'
                }}
                onClick={() => setSelectedCommunication(option.value)}
              >
                <h4 style={{ fontWeight: '500', color: '#111827', margin: '0 0 0.25rem 0' }}>{option.label}</h4>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.5rem 0' }}>{option.description}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Users style={{ height: '1rem', width: '1rem', color: '#9ca3af' }} />
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{count} recipients</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedCommunication && (
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.75rem' }}>
            Target Audience
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
            {Object.entries(audienceStats).map(([key, count]) => (
              <div
                key={key}
                style={{
                  cursor: 'pointer',
                  border: targetAudience === key ? '2px solid #7c3aed' : '2px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  padding: '0.75rem',
                  textAlign: 'center',
                  backgroundColor: targetAudience === key ? '#faf5ff' : 'white',
                  transition: 'all 0.2s'
                }}
                onClick={() => setTargetAudience(key)}
              >
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#7c3aed' }}>{count}</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'capitalize' }}>
                  {audienceLabels[key] || key.replace('_', ' ')}
                </div>
              </div>
            ))}
          </div>
          
          {currentAudience.length > 0 && (
            <button
              onClick={() => setShowPreview(!showPreview)}
              style={{
                marginTop: '0.75rem',
                fontSize: '0.875rem',
                color: '#7c3aed',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}
            >
              <Eye style={{ height: '1rem', width: '1rem' }} />
              {showPreview ? 'Hide' : 'Preview'} {currentAudience.length} recipients
            </button>
          )}
        </div>
      )}

      {showPreview && currentAudience.length > 0 && (
        <div style={{ marginBottom: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem', padding: '1rem' }}>
          <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '0.75rem' }}>Recipients Preview</h4>
          <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
            {currentAudience.slice(0, 10).map((artist, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: index < Math.min(currentAudience.length, 10) - 1 ? '1px solid #e5e7eb' : 'none' }}>
                <div>
                  <span style={{ fontWeight: '500' }}>{artist.nickname || `${artist.first_name} ${artist.last_name}`}</span>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280', marginLeft: '0.5rem' }}>{artist.email}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {artist.has_app && <Smartphone style={{ height: '1rem', width: '1rem', color: '#10b981' }} title="Has app - will receive push notification" />}
                  <Mail style={{ height: '1rem', width: '1rem', color: '#3b82f6' }} title="Will receive email" />
                </div>
              </div>
            ))}
            {currentAudience.length > 10 && (
              <div style={{ padding: '0.5rem 0', fontSize: '0.875rem', color: '#6b7280', textAlign: 'center' }}>
                ... and {currentAudience.length - 10} more recipients
              </div>
            )}
          </div>
        </div>
      )}

      {selectedCommunication === 'event_announcement' && (
        <div style={{ marginBottom: '1.5rem', backgroundColor: '#eff6ff', borderRadius: '0.5rem', padding: '1rem' }}>
          <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '0.75rem' }}>Event Details</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Event Date</label>
              <input
                type="datetime-local"
                value={eventDetails.event_date}
                onChange={(e) => setEventDetails({...eventDetails, event_date: e.target.value})}
                style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Location</label>
              <input
                type="text"
                value={eventDetails.event_location}
                onChange={(e) => setEventDetails({...eventDetails, event_location: e.target.value})}
                placeholder="Gallery address"
                style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Setup Time</label>
              <input
                type="text"
                value={eventDetails.setup_time}
                onChange={(e) => setEventDetails({...eventDetails, setup_time: e.target.value})}
                placeholder="2 hours before opening"
                style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
            </div>
          </div>
        </div>
      )}

      {selectedCommunication === 'general_update' && (
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
            Custom Message
          </label>
          <textarea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Enter your message here..."
            rows={4}
            style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
          />
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          {selectedCommunication && (
            <>
              Will send to <strong>{currentAudience.length}</strong> recipients
              {testMode && <span style={{ color: '#ea580c', marginLeft: '0.5rem' }}>(Test mode)</span>}
            </>
          )}
        </div>
        
        <button
          onClick={sendCommunication}
          disabled={!selectedCommunication || isLoading}
          style={{
            padding: '0.75rem 1.5rem',
            borderRadius: '0.375rem',
            fontWeight: '500',
            border: 'none',
            cursor: !selectedCommunication || isLoading ? 'not-allowed' : 'pointer',
            backgroundColor: !selectedCommunication || isLoading ? '#d1d5db' : testMode ? '#ea580c' : '#7c3aed',
            color: !selectedCommunication || isLoading ? '#6b7280' : 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s'
          }}
        >
          {isLoading ? (
            <>
              <div style={{ width: '1rem', height: '1rem', border: '2px solid #ffffff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
              Sending...
            </>
          ) : (
            <>
              <Send style={{ height: '1rem', width: '1rem' }} />
              {testMode ? 'Send Test' : 'Send Communication'}
            </>
          )}
        </button>
      </div>

      {results && (
        <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          borderRadius: '0.5rem',
          backgroundColor: results.success ? '#f0fdf4' : '#fef2f2',
          border: results.success ? '1px solid #bbf7d0' : '1px solid #fecaca'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            {results.success ? (
              <CheckCircle style={{ height: '1.25rem', width: '1.25rem', color: '#16a34a', marginRight: '0.5rem' }} />
            ) : (
              <AlertTriangle style={{ height: '1.25rem', width: '1.25rem', color: '#dc2626', marginRight: '0.5rem' }} />
            )}
            <h4 style={{ fontWeight: '500', color: results.success ? '#166534' : '#dc2626', margin: 0 }}>
              {results.success ? (results.test_mode ? 'Test Communication Sent!' : 'Communication Sent Successfully!') : 'Communication Failed'}
            </h4>
          </div>
          
          {results.success ? (
            <div style={{ fontSize: '0.875rem', color: '#166534' }}>
              {results.test_mode ? (
                <div>
                  <p>Preview sent to admin emails. Would reach {results.preview_data?.target_users} users.</p>
                  <p>Collection: {results.preview_data?.collection}</p>
                </div>
              ) : (
                <div>
                  <p>📧 Emails sent: {results.results?.email_sent || 0}</p>
                  <p>📱 Push notifications sent: {results.results?.push_sent || 0}</p>
                  {results.results?.errors?.length > 0 && (
                    <p style={{ color: '#d97706' }}>
                      ⚠️ {results.results.errors.length} errors occurred
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p style={{ fontSize: '0.875rem', color: '#dc2626' }}>{results.error}</p>
          )}
        </div>
      )}
    </div>
  );
};

const CollectionDetailAdmin = () => {
  // UI state for Artists tab (search/sort/pagination)
  const [artistSearch, setArtistSearch] = useState('');
  const [artistSort, setArtistSort] = useState('newest'); // name | newest | accepted | artworks | paid | needs_payment
  const [artistPage, setArtistPage] = useState(1);
  const [artistsPerPage, setArtistsPerPage] = useState(100);
  const { collectionId: collectionIdParam } = useParams();
  const navigate = useNavigate();
  
  // Convert collectionId to integer and validate
  const collectionId = collectionIdParam ? parseInt(collectionIdParam, 10) : null;
  
  // Helper to add Cloudinary named transformation
  const addCloudinaryTransform = (url, transform = 't_square-150/') => {
    if (!url || !url.includes('cloudinary.com')) return url;
    return url.replace('/upload/', `/upload/${transform}`);
  };
  
  // Helper function to detect and render HTML content
  const renderHTMLContent = (content, className = 'html-content') => {
    if (!content) return null;
    
    // Check if the content contains HTML tags
    const htmlRegex = /<[^>]*>/;
    const isHTML = htmlRegex.test(content);
    
    if (isHTML) {
      return (
        <div 
          className={className} 
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    } else {
      return content;
    }
  };

  // Helper function specifically for descriptions
  const renderDescription = (description) => {
    if (!description) return null;
    
    // Check if the description contains HTML tags
    const htmlRegex = /<[^>]*>/;
    const isHTML = htmlRegex.test(description);
    
    if (isHTML) {
      return (
        <div 
          className="detail-description html-content" 
          dangerouslySetInnerHTML={{ __html: description }}
        />
      );
    } else {
      return <p className="detail-description">{description}</p>;
    }
  };

  
  // State management
  const [collection, setCollection] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [filteredArtworks, setFilteredArtworks] = useState([]);
  const [artworkFilter, setArtworkFilter] = useState('submitted'); // 'submitted', 'accepted'
  const [artworkSort, setArtworkSort] = useState('date'); // 'order', 'title', 'artist', 'date'
  const [artists, setArtists] = useState([]);
  const [eventDetails, setEventDetails] = useState(null);
  const [collectionEvents, setCollectionEvents] = useState([]); // All events linked to this collection
  const [submissionDetails, setSubmissionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('artworks');
  const [selectedArtwork, setSelectedArtwork] = useState(null);

  const [isUpdatingMetadata, setIsUpdatingMetadata] = useState(false);
  const [metadataUpdateResult, setMetadataUpdateResult] = useState(null);
  const [loadingStages, setLoadingStages] = useState({
    collection: true,
    artworks: true,
    artists: true,
    additional: true
  });
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Purchase functionality state
  const [purchasePanel, setPurchasePanel] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [copiedPaymentLink, setCopiedPaymentLink] = useState(false);
  const [copiedPaymentLinks, setCopiedPaymentLinks] = useState(new Set()); // Track copied links per artwork
  const [generatedPaymentLinks, setGeneratedPaymentLinks] = useState(new Set()); // Track generated links in this session
  const [snackbar, setSnackbar] = useState(null); // For toast notifications
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  // Voting functionality state
  const [votingData, setVotingData] = useState(null);
  const [votingLoading, setVotingLoading] = useState(false);
  const [judgingTotalsByVcId, setJudgingTotalsByVcId] = useState({});

  // Check-in functionality
  const [checkInLoading, setCheckInLoading] = useState(false);

  // Notification state
  const [notification, setNotification] = useState(null);
  
  // Quick Story Modal state
  const [showQuickStoryModal, setShowQuickStoryModal] = useState(false);
  
  // Email copy functionality
  const [copiedEmails, setCopiedEmails] = useState(new Set());

  // Acceptances state
  const [acceptances, setAcceptances] = useState([]);
  const [acceptancesLoading, setAcceptancesLoading] = useState(true);
  const [acceptancesError, setAcceptancesError] = useState(null);
  
  // Check-in filter state
  const [checkInFilter, setCheckInFilter] = useState('all'); // 'all', 'checked-in', 'pending'
  // Search state for check-in tab
  const [checkInSearch, setCheckInSearch] = useState('');
  // Artist filter state for check-in tab
  const [checkInArtistFilter, setCheckInArtistFilter] = useState('all');
  // Infinite scroll state for check-in tab
  const [checkInDisplayCount, setCheckInDisplayCount] = useState(20);
  const [checkInLoadingMore, setCheckInLoadingMore] = useState(false);
  const checkInLoaderRef = useRef(null);

  // Attendee management state
  const [attendeeData, setAttendeeData] = useState(null);
  const [attendeeLoading, setAttendeeLoading] = useState(false);
  const [userToken, setUserToken] = useState(null);

  // Collection Activity state
  const [collectorInquiries, setCollectorInquiries] = useState([]);
  const [inquiriesLoading, setInquiriesLoading] = useState(false);
  const [inquiriesError, setInquiriesError] = useState(null);
  const [inquiryFilter, setInquiryFilter] = useState('all'); // 'all', 'collector_inquiry', 'other'
  const [inquirySort, setInquirySort] = useState('date'); // 'date', 'type', 'status'

  // Print campaign state
  const [hasPrintCampaign, setHasPrintCampaign] = useState(false);

  // Simple grid layout - no complex masonry needed
  const applyMasonryLayout = useCallback(() => {
    // This function is no longer needed - CSS Grid handles everything
    console.log('Using simple CSS Grid layout');
  }, []);

  // Simple layout - CSS Grid handles everything automatically
  useEffect(() => {
    if (filteredArtworks.length > 0) {
      console.log('Artworks loaded - CSS Grid will handle layout automatically');
    }
  }, [filteredArtworks]);

  // Load acceptances data
  const loadAcceptances = async () => {
    try {
      setAcceptancesLoading(true);
      
      // Get submissions for this collection
      const { data: submissionsData, error: submissionsError } = await supabase
        .from('submissions_details')
        .select('submission_id')
        .eq('collection_id', collectionId);
      
      if (submissionsError) throw submissionsError;
      
      if (!submissionsData || submissionsData.length === 0) {
        setAcceptances([]);
        setAcceptancesLoading(false);
        return;
      }
      
      // Get submission IDs (filter out nulls to avoid 400 error)
      const submissionIds = submissionsData
        .map(s => s.submission_id)
        .filter(id => id !== null && id !== undefined);
      
      // If no valid submission IDs, return early
      if (submissionIds.length === 0) {
        setAcceptances([]);
        setAcceptancesLoading(false);
        return;
      }
      
      // Get acceptances from artist_submission_mapping table for all submissions in this collection
      const { data, error } = await supabase
        .from('artist_submission_mapping')
        .select(`
          *,
          users (
            uuid,
            first_name,
            last_name,
            nickname,
            email
          )
        `)
        .in('submission_id', submissionIds)
        .order('accepted_at', { ascending: false });

      if (error) throw error;
      setAcceptances(data || []);
    } catch (err) {
      console.error('Error loading acceptances:', err);
      setAcceptancesError(err.message);
    } finally {
      setAcceptancesLoading(false);
    }
  };

  // Grant waiver to an artist
  const handleGrantWaiver = async (artist) => {
    try {
      // Find the submission for this artist in this collection
      const artistSubmission = artworks.find(artwork => 
        artwork.artist?.uuid === artist.uuid && artwork.accepted
      );
      
      if (!artistSubmission) {
        setNotification({ 
          type: 'error', 
          message: 'No accepted artwork found for this artist in this collection.' 
        });
        return;
      }

      // Get the submission_id from submissions_details for this collection
      const { data: submissionData, error: submissionError } = await supabase
        .from('submissions_details')
        .select('submission_id')
        .eq('collection_id', collectionId)
        .single();

      if (submissionError) {
        console.error('Error finding submission:', submissionError);
        setNotification({ 
          type: 'error', 
          message: 'Could not find submission details for this collection.' 
        });
        return;
      }

      // Check if waiver already exists
      const { data: existingWaiver } = await supabase
        .from('artist_submission_waivers')
        .select('id')
        .eq('user_id', artist.uuid)
        .eq('submission_id', submissionData.submission_id)
        .single();

      if (existingWaiver) {
        setNotification({ 
          type: 'error', 
          message: 'Fee waiver already exists for this artist and submission.' 
        });
        return;
      }

      // Create the waiver using the same logic as FeeWaiversAdmin
      const waiverData = {
        user_id: artist.uuid,
        submission_id: submissionData.submission_id,
        waiver_type: 'admin_override',
        invited_by: null, // Will be set by RLS policy
        notes: `Waiver granted by admin for ${collection?.title || 'this collection'}`,
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days
      };

      const { error: waiverError } = await supabase
        .from('artist_submission_waivers')
        .insert([waiverData]);

      if (waiverError) throw waiverError;

      // Create corresponding artist_submission_mapping record for acceptance tracking
      const mappingData = {
        user_id: artist.uuid,
        submission_id: submissionData.submission_id,
        purchase_id: null,
        paid: true,
        accepted: false,
        acceptance_uuid: crypto.randomUUID()
      };

      const { error: mappingError } = await supabase
        .from('artist_submission_mapping')
        .insert([mappingData]);

      if (mappingError) {
        console.error('Warning: Created waiver but failed to create mapping record:', mappingError);
      }

      // Reload acceptances to reflect the new waiver
      await loadAcceptances();

      setNotification({ 
        type: 'success', 
        message: `Waiver granted to ${artist.nickname || artist.first_name || artist.email}` 
      });

    } catch (err) {
      console.error('Error granting waiver:', err);
      setNotification({ 
        type: 'error', 
        message: `Failed to grant waiver: ${err.message}` 
      });
    }
  };

  // Filter artworks based on check-in status and search
  const getFilteredArtworks = () => {
    // Start with only ACCEPTED artworks for check-in
    let filtered = artworks.filter(art => art.accepted === true);
    
    // Then filter by check-in status
    if (eventDetails?.gallery_id) {
      switch (checkInFilter) {
        case 'checked-in':
          filtered = filtered.filter(art => art.location === eventDetails.gallery_id);
          break;
        case 'pending':
          filtered = filtered.filter(art => art.location !== eventDetails.gallery_id);
          break;
        default:
          // 'all' keeps all accepted artworks
          break;
      }
    }
    
    // Filter by artist if selected
    if (checkInArtistFilter !== 'all') {
      filtered = filtered.filter(art => art.artist?.uuid === checkInArtistFilter);
    }
    
    // Then filter by search term
    if (checkInSearch.trim()) {
      const searchTerm = checkInSearch.toLowerCase().trim();
      filtered = filtered.filter(art => {
        const title = (art.title || '').toLowerCase();
        const artistName = (art.artist?.nickname || 
                           `${art.artist?.first_name || ''} ${art.artist?.last_name || ''}`.trim() || 
                           art.artist_name || '').toLowerCase();
        
        return title.includes(searchTerm) || artistName.includes(searchTerm);
      });
    }
    
    return filtered;
  };

  // Get paginated check-in artworks
  const allCheckInArtworks = getFilteredArtworks();
  const displayedCheckInArtworks = allCheckInArtworks.slice(0, checkInDisplayCount);
  const hasMoreCheckIn = checkInDisplayCount < allCheckInArtworks.length;

  // Reset check-in display count when filters/search change
  useEffect(() => {
    setCheckInDisplayCount(20);
  }, [checkInFilter, checkInSearch, checkInArtistFilter]);

  // Load more check-in artworks
  const loadMoreCheckIn = useCallback(() => {
    if (checkInLoadingMore || !hasMoreCheckIn) return;
    
    setCheckInLoadingMore(true);
    setTimeout(() => {
      setCheckInDisplayCount(prev => Math.min(prev + 20, allCheckInArtworks.length));
      setCheckInLoadingMore(false);
    }, 300);
  }, [checkInLoadingMore, hasMoreCheckIn, allCheckInArtworks.length]);

  // Intersection Observer for check-in infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreCheckIn && !checkInLoadingMore) {
          loadMoreCheckIn();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (checkInLoaderRef.current) {
      observer.observe(checkInLoaderRef.current);
    }

    return () => observer.disconnect();
  }, [loadMoreCheckIn, hasMoreCheckIn, checkInLoadingMore]);

  // Check-in artwork function
  const handleCheckInArtwork = async (artwork) => {
    // Save scroll position before any state changes
    const savedScrollY = window.scrollY;
    
    if (!eventDetails?.gallery_id) {
      setNotification({ 
        type: 'error', 
        message: 'No gallery ID found for this event. Please check event details.' 
      });
      return;
    }

    // Debug: Log event details to see what's available
    console.log('🔧 Event Details:', eventDetails);
    console.log('🔧 Event Name:', eventDetails?.name);
    console.log('🔧 Collection Title:', collection?.title);

    setCheckInLoading(true);
    try {
      // 1. Update artwork location in vcs table
      const { error: updateError } = await supabase
        .from('vcs')
        .update({ 
          location: eventDetails.gallery_id,
          updated_at: new Date().toISOString()
        })
        .eq('vc_id', artwork.vc_id);

      if (updateError) throw updateError;

      // 2. Send notifications to artist
      const eventTitle = eventDetails?.name || collection?.title || 'the event';
      const artistName = artwork.artist.nickname || artwork.artist.first_name || 'Artist';
      const artistEmail = artwork.artist.email;

      // Push notification
      try {
        const { data: notificationData, error: notificationError } = await supabase.functions.invoke('onesignal_push_artist_v2', {
          body: {
            externalId: artwork.artist.uuid,
            title: "🎨 Artwork Checked In!",
            message: `"${artwork.title}" has been checked in for ${eventTitle}! 📱`,
            category: "artwork_checkin",
            priority: 2,
            metadata: {
              artwork_id: artwork.vc_id,
              artwork_title: artwork.title,
              event_name: eventTitle,
              gallery_id: eventDetails.gallery_id,
              action_url: `/artwork/${artwork.vc_id}`,
              checkin_timestamp: new Date().toISOString()
            }
          }
        });

        if (notificationError) {
          console.warn('Push notification failed:', notificationError);
        }
      } catch (notificationError) {
        console.warn('Push notification failed:', notificationError);
      }

      // Email confirmation
      if (artistEmail) {
        try {
          await supabase.functions.invoke('send-mandrill-email', {
            body: {
              template_name: 'admin-notification',
              subject: `Your artwork has been checked in for ${eventTitle}`,
              message: {
                to: [{ email: artistEmail, name: artistName }],
                global_merge_vars: [
                  { name: 'FIRST_NAME', content: artistName },
                  { name: 'SUBJECT', content: `Your artwork has been checked in for ${eventTitle}` },
                  { name: 'BODY', content: `<p>Hi ${artistName},</p><p>Great news! Your artwork <strong>"${artwork.title}"</strong> has been successfully checked in for <strong>${eventTitle}</strong>.</p><p>If you have any questions about the event, please don't hesitate to reach out.</p><p>Thank you for participating!</p>` }
                ]
              }
            }
          });
        } catch (emailError) {
          console.warn('Check-in confirmation email failed:', emailError);
        }
      }

      // 3. Update local state
      setArtworks(prev => prev.map(art => 
        art.vc_id === artwork.vc_id 
          ? { ...art, location: eventDetails.gallery_id }
          : art
      ));

      setNotification({ 
        type: 'success', 
        message: `"${artwork.title}" has been checked in successfully!` 
      });

    } catch (error) {
      console.error('Error checking in artwork:', error);
      setNotification({ 
        type: 'error', 
        message: `Failed to check in artwork: ${error.message}` 
      });
    } finally {
      setCheckInLoading(false);
      setTimeout(() => setNotification(null), 5000);
      // Restore scroll position after state updates
      requestAnimationFrame(() => {
        window.scrollTo(0, savedScrollY);
      });
    }
  };

  // Un-check-in artwork function (sets location to null)
  const handleUnCheckInArtwork = async (artwork) => {
    // Save scroll position before any state changes
    const savedScrollY = window.scrollY;
    
    setCheckInLoading(true);
    try {
      // Update artwork location in vcs table to null
      const { error: updateError } = await supabase
        .from('vcs')
        .update({ 
          location: null,
          updated_at: new Date().toISOString()
        })
        .eq('vc_id', artwork.vc_id);

      if (updateError) throw updateError;

      // Update local state
      setArtworks(prev => prev.map(art => 
        art.vc_id === artwork.vc_id 
          ? { ...art, location: null }
          : art
      ));

      setNotification({ 
        type: 'success', 
        message: `"${artwork.title}" has been un-checked. Location set to null.` 
      });

    } catch (error) {
      console.error('Error un-checking artwork:', error);
      setNotification({ 
        type: 'error', 
        message: `Failed to un-check artwork: ${error.message}` 
      });
    } finally {
      setCheckInLoading(false);
      setTimeout(() => setNotification(null), 5000);
      // Restore scroll position after state updates
      requestAnimationFrame(() => {
        window.scrollTo(0, savedScrollY);
      });
    }
  };

  // Bulk check-in all accepted artworks across the entire collection
  const handleBulkCheckInAll = async () => {
    const savedScrollY = window.scrollY;

    if (!eventDetails?.gallery_id) {
      setNotification({ type: 'error', message: 'No gallery ID found for this event. Please check event details.' });
      return;
    }

    const pendingArtworks = artworks.filter(art =>
      art.accepted === true && art.location !== eventDetails.gallery_id
    );

    if (pendingArtworks.length === 0) {
      setNotification({ type: 'info', message: 'All accepted artworks are already checked in.' });
      return;
    }

    setCheckInLoading(true);
    let successCount = 0;
    let failureCount = 0;
    const eventTitle = eventDetails?.name || collection?.title || 'the event';

    try {
      for (const artwork of pendingArtworks) {
        try {
          const { error } = await supabase
            .from('vcs')
            .update({ location: eventDetails.gallery_id, updated_at: new Date().toISOString() })
            .eq('vc_id', artwork.vc_id);
          if (error) throw error;
          successCount++;
        } catch (error) {
          console.error(`Error checking in artwork ${artwork.title}:`, error);
          failureCount++;
        }
      }

      setArtworks(prev => prev.map(art =>
        pendingArtworks.some(pa => pa.vc_id === art.vc_id)
          ? { ...art, location: eventDetails.gallery_id }
          : art
      ));

      if (successCount > 0 && failureCount === 0) {
        setNotification({ type: 'success', message: `Successfully checked in all ${successCount} artworks!` });
      } else if (successCount > 0) {
        setNotification({ type: 'warning', message: `Checked in ${successCount} artworks. ${failureCount} failed.` });
      } else {
        setNotification({ type: 'error', message: 'Failed to check in artworks.' });
      }
    } catch (error) {
      setNotification({ type: 'error', message: `Bulk check-in failed: ${error.message}` });
    } finally {
      setCheckInLoading(false);
      setTimeout(() => setNotification(null), 5000);
      requestAnimationFrame(() => window.scrollTo(0, savedScrollY));
    }
  };

  // Bulk check-out all checked-in artworks across the entire collection
  const handleBulkCheckOutAll = async () => {
    const savedScrollY = window.scrollY;

    if (!eventDetails?.gallery_id) {
      setNotification({ type: 'error', message: 'No gallery ID found for this event. Please check event details.' });
      return;
    }

    const checkedInArtworks = artworks.filter(art =>
      art.accepted === true && art.location === eventDetails.gallery_id
    );

    if (checkedInArtworks.length === 0) {
      setNotification({ type: 'info', message: 'No checked-in artworks to check out.' });
      return;
    }

    setCheckInLoading(true);
    let successCount = 0;
    let failureCount = 0;

    try {
      for (const artwork of checkedInArtworks) {
        try {
          const { error } = await supabase
            .from('vcs')
            .update({ location: null, updated_at: new Date().toISOString() })
            .eq('vc_id', artwork.vc_id);
          if (error) throw error;
          successCount++;
        } catch (error) {
          console.error(`Error checking out artwork ${artwork.title}:`, error);
          failureCount++;
        }
      }

      setArtworks(prev => prev.map(art =>
        checkedInArtworks.some(ca => ca.vc_id === art.vc_id)
          ? { ...art, location: null }
          : art
      ));

      if (successCount > 0 && failureCount === 0) {
        setNotification({ type: 'success', message: `Successfully checked out all ${successCount} artworks.` });
      } else if (successCount > 0) {
        setNotification({ type: 'warning', message: `Checked out ${successCount} artworks. ${failureCount} failed.` });
      } else {
        setNotification({ type: 'error', message: 'Failed to check out artworks.' });
      }
    } catch (error) {
      setNotification({ type: 'error', message: `Bulk check-out failed: ${error.message}` });
    } finally {
      setCheckInLoading(false);
      setTimeout(() => setNotification(null), 5000);
      requestAnimationFrame(() => window.scrollTo(0, savedScrollY));
    }
  };

  // Bulk check-in function for all artworks from a specific artist
  const handleBulkCheckIn = async (artistUuid) => {
    // Save scroll position before any state changes
    const savedScrollY = window.scrollY;
    
    if (!eventDetails?.gallery_id) {
      setNotification({ 
        type: 'error', 
        message: 'No gallery ID found for this event. Please check event details.' 
      });
      return;
    }

    // Get all pending artworks from this artist
    const artistArtworks = artworks.filter(art => 
      art.artist?.uuid === artistUuid && 
      art.location !== eventDetails.gallery_id
    );

    if (artistArtworks.length === 0) {
      setNotification({ 
        type: 'info', 
        message: 'No pending artworks to check in for this artist.' 
      });
      return;
    }

    const artistName = artists.find(a => a.uuid === artistUuid)?.nickname || 
                      `${artists.find(a => a.uuid === artistUuid)?.first_name} ${artists.find(a => a.uuid === artistUuid)?.last_name}`;

    setCheckInLoading(true);
    let successCount = 0;
    let failureCount = 0;
    const eventTitle = eventDetails?.name || collection?.title || 'the event';

    try {
      // Process each artwork
      for (const artwork of artistArtworks) {
        try {
          // Update artwork location in vcs table
          const { error: updateError } = await supabase
            .from('vcs')
            .update({ 
              location: eventDetails.gallery_id,
              updated_at: new Date().toISOString()
            })
            .eq('vc_id', artwork.vc_id);

          if (updateError) throw updateError;

          // Send push notification (optional - don't fail if this fails)
          try {
            await supabase.functions.invoke('onesignal_push_artist_v2', {
              body: {
                externalId: artwork.artist.uuid,
                title: "🎨 Artwork Checked In!",
                message: `"${artwork.title}" has been checked in for ${eventTitle}! 📱`,
                category: "artwork_checkin",
                priority: 2,
                metadata: {
                  artwork_id: artwork.vc_id,
                  artwork_title: artwork.title,
                  event_name: eventTitle,
                  gallery_id: eventDetails.gallery_id,
                  action_url: `/artwork/${artwork.vc_id}`,
                  checkin_timestamp: new Date().toISOString()
                }
              }
            });
          } catch (notificationError) {
            console.warn(`Push notification failed for artwork ${artwork.title}:`, notificationError);
          }

          // Send check-in confirmation email (optional - don't fail if this fails)
          const bulkArtistEmail = artwork.artist?.email;
          if (bulkArtistEmail) {
            try {
              const bulkArtistName = artwork.artist?.nickname || artwork.artist?.first_name || 'Artist';
              await supabase.functions.invoke('send-mandrill-email', {
                body: {
                  template_name: 'admin-notification',
                  subject: `Your artwork has been checked in for ${eventTitle}`,
                  message: {
                    to: [{ email: bulkArtistEmail, name: bulkArtistName }],
                    global_merge_vars: [
                      { name: 'FIRST_NAME', content: bulkArtistName },
                      { name: 'SUBJECT', content: `Your artwork has been checked in for ${eventTitle}` },
                      { name: 'BODY', content: `<p>Hi ${bulkArtistName},</p><p>Great news! Your artwork <strong>"${artwork.title}"</strong> has been successfully checked in for <strong>${eventTitle}</strong>.</p><p>If you have any questions about the event, please don't hesitate to reach out.</p><p>Thank you for participating!</p>` }
                    ]
                  }
                }
              });
            } catch (emailError) {
              console.warn(`Check-in confirmation email failed for artwork ${artwork.title}:`, emailError);
            }
          }

          successCount++;
        } catch (error) {
          console.error(`Error checking in artwork ${artwork.title}:`, error);
          failureCount++;
        }
      }

      // Update local state for all successful check-ins
      setArtworks(prev => prev.map(art => 
        artistArtworks.some(aa => aa.vc_id === art.vc_id) && successCount > 0
          ? { ...art, location: eventDetails.gallery_id }
          : art
      ));

      // Show result notification
      if (successCount > 0 && failureCount === 0) {
        setNotification({ 
          type: 'success', 
          message: `Successfully checked in ${successCount} artworks from ${artistName}!` 
        });
      } else if (successCount > 0 && failureCount > 0) {
        setNotification({ 
          type: 'warning', 
          message: `Checked in ${successCount} artworks from ${artistName}. ${failureCount} failed.` 
        });
      } else {
        setNotification({ 
          type: 'error', 
          message: `Failed to check in artworks from ${artistName}.` 
        });
      }

    } catch (error) {
      console.error('Error in bulk check-in:', error);
      setNotification({ 
        type: 'error', 
        message: `Bulk check-in failed: ${error.message}` 
      });
    } finally {
      setCheckInLoading(false);
      setTimeout(() => setNotification(null), 5000);
      // Restore scroll position after state updates
      requestAnimationFrame(() => {
        window.scrollTo(0, savedScrollY);
      });
    }
  };



  // Helper function to enrich artists with communication data
  const enrichArtistsWithCommunicationData = async (artistList, collectionId) => {
    if (!artistList || artistList.length === 0) return [];
    if (!collectionId || isNaN(collectionId)) {
      console.warn('Invalid collectionId provided to enrichArtistsWithCommunicationData:', collectionId);
      return artistList.map(artist => ({
        ...artist,
        has_app: false,
        has_paid: false,
        has_submitted_artwork: false
      }));
    }
    
    try {
      // Get all artist UUIDs
      const artistUuids = artistList.map(artist => artist.uuid);
      
      // Batch all queries in parallel instead of per-artist
      const [playerIdsResult, submissionMappingResult, artistVcsResult, collectionArtworksResult] = await Promise.all([
        // Get player IDs for all artists at once
        supabase
          .from('user_player_ids')
          .select('user_id, player_id')
          .in('user_id', artistUuids),
        
        // Get submission mappings for all artists at once - check paid status
        supabase
          .from('artist_submission_mapping')
          .select('user_id, paid, purchase_id')
          .in('user_id', artistUuids)
          .eq('paid', true),
        
        // Get all VCs for these artists at once
        supabase
          .from('vcs')
          .select('uuid, vc_id')
          .in('uuid', artistUuids),
        
        // Get collection artworks once
        supabase
          .from('collection_vc_mapping')
          .select('vc_id')
          .eq('collection_id', collectionId)
      ]);

      // Create lookup maps for O(1) lookups instead of filtering arrays
      const playerIdMap = new Set(
        (playerIdsResult.data || []).map(item => item.user_id)
      );
      
      const submissionMap = new Set(
        (submissionMappingResult.data || []).map(item => item.user_id)
      );
      
      const artistVcMap = new Map();
      (artistVcsResult.data || []).forEach(vc => {
        if (!artistVcMap.has(vc.uuid)) {
          artistVcMap.set(vc.uuid, []);
        }
        artistVcMap.get(vc.uuid).push(vc.vc_id);
      });
      
      const collectionVcIds = new Set(
        (collectionArtworksResult.data || []).map(item => item.vc_id)
      );

      // Now process all artists with O(1) lookups
      return artistList.map(artist => {
        const artistVcIds = artistVcMap.get(artist.uuid) || [];
        const hasSubmittedArtwork = artistVcIds.some(vcId => collectionVcIds.has(vcId));
        
        return {
          ...artist,
          has_app: playerIdMap.has(artist.uuid),
          has_paid: submissionMap.has(artist.uuid),
          has_submitted_artwork: hasSubmittedArtwork
        };
      });
    } catch (error) {
      console.error('Error in batch enriching artists:', error);
      return artistList.map(artist => ({
        ...artist,
        has_app: false,
        has_paid: false,
        has_submitted_artwork: false
      }));
    }
  };

  // Load collection data using the RPC function
  useEffect(() => {
    loadCollectionData();
    loadAcceptances();
  }, [collectionId]);

  // Load attendee data when event tab is selected
  useEffect(() => {
    if (activeTab === 'event' && eventDetails?.eventbrite_id && !attendeeData) {
      loadAttendeeData();
    }
  }, [activeTab, eventDetails]);

  // Load user token for API calls
  useEffect(() => {
    const loadUserToken = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          setUserToken(session.access_token);
        }
      } catch (error) {
        console.error('Failed to load user token:', error);
      }
    };
    
    loadUserToken();
  }, []);

  const loadCollectionData = async () => {
    if (!collectionId || isNaN(collectionId)) {
      console.warn('Invalid collectionId provided to loadCollectionData:', collectionId);
      setError('Invalid collection ID');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setLoadingProgress(0);
      setLoadingStages({
        collection: true,
        artworks: true,
        artists: true,
        additional: true
      });

      // Stage 1: Get collection details directly (much faster than RPC which fetches ALL collections)
      setLoadingProgress(10);
      const { data: collectionData, error: collectionError } = await supabase
        .from('collections')
        .select('*')
        .eq('collection_id', collectionId)
        .single();

      if (collectionError) throw collectionError;

      if (!collectionData) {
        throw new Error('Collection not found');
      }

      console.log('Found Collection:', collectionData);

      setCollection(collectionData);
      setLoadingStages(prev => ({ ...prev, collection: false }));
      setLoadingProgress(25);

      // Stage 2: Load artworks directly from collection_vc_mapping
      setLoadingProgress(40);
      await loadArtworksDirectly(collectionId);
      setLoadingStages(prev => ({ ...prev, artworks: false }));
      setLoadingProgress(70);

      // Stage 3: Load additional data in parallel
      setLoadingProgress(85);
      try {
        await Promise.allSettled([
          loadEventData(collectionId),
          loadSubmissionData(collectionId),
          loadPrintCampaignData(collectionId)
        ]);
      } catch (err) {
        console.error('Error loading additional data:', err);
        // Continue with loading even if this fails
      }
      setLoadingStages(prev => ({ ...prev, additional: false }));
      setLoadingProgress(100);

      // Loading complete - no artificial delay
      setLoading(false);

    } catch (err) {
      console.error('Error loading collection:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const loadArtistDataFromArtworks = async (artworksList, collectionId) => {
    try {
      console.log('🎨 Loading artist data from artworks:', artworksList.length, 'artworks');
      
      // Get unique artists from the artworks in this collection
      const uniqueArtists = new Map();
      
      artworksList.forEach(artwork => {
        if (artwork.artist && artwork.artist.uuid) {
          const artistId = artwork.artist.uuid;
          console.log('🧑‍🎨 Processing artist:', artistId, 'Email:', artwork.artist.email);
          
          if (!uniqueArtists.has(artistId)) {
            const artistData = {
              uuid: artistId,
              first_name: artwork.artist.first_name,
              last_name: artwork.artist.last_name,
              nickname: artwork.artist.nickname,
              handle: artwork.artist.handle,
              profile_img: artwork.artist.profile_img,
              bio: artwork.artist.bio || 'No bio available',
              email: artwork.artist.email, // Add email for communication
              mobile_phone: artwork.artist.mobile_phone, // Add mobile phone for quick contact
              artwork_count_in_collection: 1,
              joined_collection: artwork.created_at
            };
            console.log('✨ Creating new artist entry:', artistData);
            uniqueArtists.set(artistId, artistData);
          } else {
            // Increment artwork count for this artist
            const artist = uniqueArtists.get(artistId);
            artist.artwork_count_in_collection += 1;
            // Use the earliest creation date as "joined" date
            if (new Date(artwork.created_at) < new Date(artist.joined_collection)) {
              artist.joined_collection = artwork.created_at;
            }
          }
        }
      });

      const artistsArray = Array.from(uniqueArtists.values());
      console.log('📋 Final artists array before enrichment:', artistsArray);
      
      // Enrich with communication data
      const enrichedArtists = await enrichArtistsWithCommunicationData(artistsArray, collectionId);
      console.log('🚀 Final enriched artists:', enrichedArtists);
      setArtists(enrichedArtists);
      setLoadingStages(prev => ({ ...prev, artists: false }));
    } catch (err) {
      console.error('Error loading artist data:', err);
      // Set empty array if there's an error
      setArtists([]);
      setLoadingStages(prev => ({ ...prev, artists: false }));
    }
  };

  const loadArtworksDirectly = async (collectionId) => {
    if (!collectionId || isNaN(collectionId)) {
      console.warn('Invalid collectionId provided to loadArtworksDirectly:', collectionId);
      return;
    }
    try {
      console.log('=== DIRECT LOADING STARTED ===');
      console.log('Loading artworks directly for collection:', collectionId);
      
      // Get artworks directly from collection_vc_mapping and vcs tables
      const { data: artworkData, error: artworkError } = await supabase
        .from('collection_vc_mapping')
        .select(`
          vc_id,
          accepted,
          order_num,
          created_at,
          vcs (
            vc_id,
            title,
            description,
            cover_image,
            access_control,
            created_at,
            updated_at,
            width,
            height,
            depth,
            price,
            status,
            more,
            uuid,
            location,
            payment_link,
            artwork_completed_date,
            users (
              uuid,
              first_name,
              last_name,
              nickname,
              handle,
              profile_img,
              email,
              bio,
              mobile_phone
            )
          )
        `)
        .eq('collection_id', collectionId)
        .order('order_num', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });

      console.log('Direct artwork query result:', { artworkData, artworkError });

      if (artworkError) {
        console.error('Artwork error:', artworkError);
        return;
      }

      if (!artworkData || artworkData.length === 0) {
        console.log('No artworks found in collection_vc_mapping for collection:', collectionId);
        setArtworks([]);
        setArtists([]);
        return;
      }

      // Process artwork data - filter to only include Public artworks for consistency
      const artworkList = artworkData?.map(item => ({
        ...item.vcs,
        artist: item.vcs.users,
        // Add mapping data
        accepted: item.accepted,
        order_num: item.order_num,
        submission_date: item.created_at // Use the mapping table's created_at as submission date
      })).filter(artwork => artwork.access_control === 'Public') || [];

      console.log('Processed artwork list (Public only):', artworkList.length);

      // Set artworks directly without fetching engagement metrics (not displayed on this page)
      setArtworks(artworkList);
      
      // Load artist data from the artworks
      if (artworkList.length > 0) {
        await loadArtistDataFromArtworks(artworkList, collectionId);
      } else {
        console.log('No artworks found even with direct loading');
        setArtists([]);
      }

      console.log('=== DIRECT LOADING COMPLETED ===');
      console.log('Final artworks count:', artworkList.length);
      console.log('Sample artwork:', artworkList[0]);

    } catch (err) {
      console.error('Error loading artworks directly:', err);
      setArtworks([]);
      setArtists([]);
    }
  };

  const loadEventData = async (collectionId) => {
    if (!collectionId || isNaN(collectionId)) {
      console.warn('Invalid collectionId provided to loadEventData:', collectionId);
      return;
    }
    try {
      // Load ALL events linked to this collection (supports multiple events per collection)
      const { data: eventsData, error } = await supabase
        .from('events')
        .select(`
          *,
          galleries:gallery_id (
            gallery_id,
            name,
            address,
            website,
            contact_email,
            contact_phone,
            cover_image,
            logo
          ),
          event_ticket_types (
            id,
            ticket_type_name,
            regular_price,
            tickets_available,
            tickets_sold,
            is_active
          )
        `)
        .eq('collection_id', collectionId)
        .order('event_start', { ascending: true });

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 is "no rows returned"
      
      if (eventsData && eventsData.length > 0) {
        // Enrich events with aggregated ticket data from event_ticket_types
        const enrichedEvents = eventsData.map(event => {
          const ticketTypes = event.event_ticket_types || [];
          const totalSold = ticketTypes.reduce((sum, tt) => sum + (tt.tickets_sold || 0), 0);
          const totalAvailable = ticketTypes.reduce((sum, tt) => sum + (tt.tickets_available || 0), 0);
          const totalRevenue = ticketTypes.reduce((sum, tt) => sum + ((tt.tickets_sold || 0) * parseFloat(tt.regular_price || 0)), 0);
          return {
            ...event,
            tickets_sold: totalSold,
            tickets_available: ticketTypes.length > 0 ? totalAvailable : null,
            total_revenue: totalRevenue,
          };
        });

        // Store all events
        setCollectionEvents(enrichedEvents);
        
        // Set the first (or upcoming) event as eventDetails for backward compatibility
        // Find the next upcoming event, or use the first one
        const now = new Date();
        const upcomingEvent = enrichedEvents.find(e => new Date(e.event_start) >= now) || enrichedEvents[0];
        setEventDetails(upcomingEvent);
        
        // Pre-load attendee data if this is an active event with Eventbrite
        if (upcomingEvent.eventbrite_id && upcomingEvent.status === 'active') {
          loadAttendeeData();
        }
      }
    } catch (err) {
      console.error('Error loading event data:', err);
    }
  };

  const loadSubmissionData = async (collectionId) => {
    if (!collectionId || isNaN(collectionId)) {
      console.warn('Invalid collectionId provided to loadSubmissionData:', collectionId);
      return;
    }
    try {
      // Check if this collection has submission details
      const { data: submissionData, error } = await supabase
        .from('submissions_details')
        .select('*')
        .eq('collection_id', collectionId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (submissionData) {
        setSubmissionDetails(submissionData);
      }
    } catch (err) {
      console.error('Error loading submission data:', err);
    }
  };

  // Load print campaign data for this collection
  const loadPrintCampaignData = async (collectionId) => {
    if (!collectionId || isNaN(collectionId)) {
      return;
    }
    try {
      const { data: campaignData, error } = await supabase
        .from('print_campaigns')
        .select('campaign_id')
        .eq('collection_id', collectionId)
        .eq('active', true)
        .maybeSingle();

      if (error) throw error;
      setHasPrintCampaign(!!campaignData);
    } catch (err) {
      console.error('Error loading print campaign data:', err);
    }
  };

  const loadCollectorInquiries = async () => {
    setInquiriesLoading(true);
    setInquiriesError(null);
    
    try {
      // Get all vc_ids for artworks in this collection
      const vcIds = artworks.map(artwork => artwork.vc_id).filter(Boolean);
      
      if (vcIds.length === 0) {
        setCollectorInquiries([]);
        return;
      }

      // Load notifications for these artworks
      const { data: notifications, error } = await supabase
        .from('notifications')
        .select(`
          id,
          type,
          category,
          title,
          body,
          metadata,
          created_at,
          read,
          user_id,
          sender_id,
          vc_id,
          gallery_id,
          image_url,
          action_url,
          priority
        `)
        .in('vc_id', vcIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get unique user IDs for both senders and recipients
      const userIds = [...new Set([
        ...notifications.map(n => n.user_id).filter(Boolean),
        ...notifications.map(n => n.sender_id).filter(Boolean)
      ])];

      // Fetch user information for all involved users
      let usersData = {};
      if (userIds.length > 0) {
        const { data: users, error: usersError } = await supabase
          .from('users')
          .select('uuid, first_name, last_name, nickname, email, profile_img')
          .in('uuid', userIds);

        if (usersError) {
          console.error('Error fetching users:', usersError);
        } else {
          // Create a lookup map for users
          usersData = users.reduce((acc, user) => {
            acc[user.uuid] = user;
            return acc;
          }, {});
        }
      }

      // Process and enrich notification data
      const processedInquiries = notifications.map(notification => {
        const metadata = typeof notification.metadata === 'string' ? 
          JSON.parse(notification.metadata) : notification.metadata || {};
        
        // Find the artwork this inquiry is about
        const artwork = artworks.find(art => art.vc_id === notification.vc_id);
        
        // Get sender and recipient user info
        const senderUser = usersData[notification.sender_id];
        const recipientUser = usersData[notification.user_id];
        
        return {
          ...notification,
          metadata,
          artwork,
          sender_user: senderUser,
          recipient_user: recipientUser,
          priority: notification.category === 'collector_inquiry' ? 1 : 2, // Prioritize collector inquiries
          formatted_date: new Date(notification.created_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        };
      });

      setCollectorInquiries(processedInquiries);
    } catch (err) {
      console.error('Error loading collector inquiries:', err);
      setInquiriesError(err.message);
    } finally {
      setInquiriesLoading(false);
    }
  };

  // Filter and sort artworks
  useEffect(() => {
    let filtered = [...artworks];
    
    // Apply filter
    if (artworkFilter === 'accepted') {
      filtered = filtered.filter(artwork => artwork.accepted === true);
    } else if (artworkFilter === 'not_accepted') {
      filtered = filtered.filter(artwork => artwork.accepted === false);
    } else if (artworkFilter === 'submitted') {
      // Show ALL artworks (accepted=true, accepted=null, accepted=false)
      // Don't filter at all - show everything
      filtered = [...artworks];
    }
    
    // Apply sort
    filtered.sort((a, b) => {
      switch (artworkSort) {
        case 'order':
          // Order by order_num first (nulls last), then by submission date
          if (a.order_num === null && b.order_num === null) {
            return new Date(b.submission_date) - new Date(a.submission_date);
          }
          if (a.order_num === null) return 1;
          if (b.order_num === null) return -1;
          return a.order_num - b.order_num;
        
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        
        case 'artist':
          const artistA = a.artist?.nickname || `${a.artist?.first_name} ${a.artist?.last_name}` || '';
          const artistB = b.artist?.nickname || `${b.artist?.first_name} ${b.artist?.last_name}` || '';
          return artistA.localeCompare(artistB);
        
        case 'date':
          return new Date(b.submission_date) - new Date(a.submission_date);
        
        default:
          return 0;
      }
    });
    
    setFilteredArtworks(filtered);
  }, [artworks, artworkFilter, artworkSort]);

  // Load collector inquiries when artworks are loaded
  useEffect(() => {
    if (artworks.length > 0 && activeTab === 'inquiries') {
      loadCollectorInquiries();
    }
  }, [artworks, activeTab]);

  // Toggle artwork acceptance status
  const toggleAcceptanceStatus = async (artwork) => {
    try {
      const newAcceptedStatus = !artwork.accepted;
      
      // Update the database
      const { error } = await supabase
        .from('collection_vc_mapping')
        .update({ accepted: newAcceptedStatus })
        .eq('collection_id', collectionId)
        .eq('vc_id', artwork.vc_id);

      if (error) throw error;

      // Update local state
      setArtworks(prevArtworks => 
        prevArtworks.map(a => 
          a.vc_id === artwork.vc_id 
            ? { ...a, accepted: newAcceptedStatus }
            : a
        )
      );

      console.log(`Artwork ${artwork.title} ${newAcceptedStatus ? 'accepted' : 'submitted for review'}`);
      
    } catch (err) {
      console.error('Error updating acceptance status:', err);
      // You might want to show a toast notification here
    }
  };

  // Remove artwork from collection
  const removeFromCollection = async (artwork) => {
    const confirmMessage = `Are you sure you want to remove "${artwork.title}" from this collection?\n\nThis will delete the artwork from the collection but will NOT delete the artwork itself.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('collection_vc_mapping')
        .delete()
        .eq('collection_id', collectionId)
        .eq('vc_id', artwork.vc_id);

      if (error) throw error;

      // Update local state - remove the artwork from the list
      setArtworks(prevArtworks => 
        prevArtworks.filter(a => a.vc_id !== artwork.vc_id)
      );

      setNotification({
        type: 'success',
        message: `"${artwork.title}" has been removed from the collection.`
      });

      console.log(`Artwork ${artwork.title} removed from collection`);
      
      // Auto-hide notification after 5 seconds
      setTimeout(() => setNotification(null), 5000);
      
    } catch (err) {
      console.error('Error removing artwork from collection:', err);
      setNotification({
        type: 'error',
        message: `Failed to remove artwork: ${err.message}`
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const goToPdfGenerator = () => {
    navigate(`/printReactCards/${collectionId}`);
  };

  const goToWallPlanner = () => {
    navigate(`/gallery-wall-planner/${collectionId}?view=topdown`);
  };

  const goTo3DEditor = () => {
    // Navigate to the 3D Editor which has the control panels
    navigate(`/gallery-3d-editor?collection_id=${collectionId}`);
  };

  const goTo3DViewer = () => {
    // Navigate to the 3D Viewer which is read-only
    window.open(`/gallery-3d?collection_id=${collectionId}`, '_blank');
  };

  const toggleFeatureStatus = async () => {
    try {
      const { error } = await supabase
        .from('collections')
        .update({ featured: !collection.is_currently_featured })
        .eq('collection_id', collectionId);

      if (error) throw error;

      setCollection(prev => ({
        ...prev,
        is_currently_featured: !prev.is_currently_featured
      }));
    } catch (err) {
      console.error('Error updating feature status:', err);
    }
  };



  // Add this function to handle the metadata update action
  const handleUpdateMetadata = async () => {
    if (!collectionId || isNaN(collectionId)) {
      console.warn('Invalid collectionId provided to handleUpdateMetadata:', collectionId);
      setMetadataUpdateResult({
        success: false,
        message: 'Invalid collection ID'
      });
      return;
    }
    
    setIsUpdatingMetadata(true);
    setMetadataUpdateResult(null);
    
    try {
      const result = await updateCollectionArtworkMetadata(collectionId);
      setMetadataUpdateResult({
        success: true,
        message: result.message,
        details: result
      });
      
      // Show success message for 3 seconds
      setTimeout(() => {
        setMetadataUpdateResult(null);
      }, 3000);
      
    } catch (error) {
      setMetadataUpdateResult({
        success: false,
        message: error.message || 'Failed to update artwork metadata'
      });
      
      // Show error message for 5 seconds
      setTimeout(() => {
        setMetadataUpdateResult(null);
      }, 5000);
    } finally {
      setIsUpdatingMetadata(false);
    }
  };

  // Attendee management helper functions
  const loadAttendeeData = async () => {
    if (!eventDetails?.eventbrite_id || !userToken) return;
    
    setAttendeeLoading(true);
    try {
      const apiService = new EventbriteApiService(userToken);
      
      // First check if attendees data exists
      const hasData = await apiService.checkAttendeesExist(eventDetails.eventbrite_id);
      console.log('Has attendees data:', hasData);
      
      if (!hasData) {
        console.log('No attendees data found, checking Eventbrite event...');
        
        try {
          // Check if the Eventbrite event exists and has attendees
          const eventbriteEvent = await apiService.checkEventbriteEvent(eventDetails.eventbrite_id);
          console.log('Eventbrite event check:', eventbriteEvent);
          
          if (!eventbriteEvent.exists) {
            console.error('Eventbrite event not found:', eventDetails.eventbrite_id);
            NotificationService.showError('Eventbrite event not found');
            return;
          }
          
          if (eventbriteEvent.attendee_count === 0) {
            console.log('Eventbrite event has no attendees');
            NotificationService.showInfo('Event has no attendees yet');
            return;
          }
          
          console.log('Syncing attendees from Eventbrite...');
          NotificationService.showInfo('Syncing attendees from Eventbrite...');
          
          // Sync attendees first
          const syncResult = await apiService.syncEventAttendees(eventDetails.eventbrite_id);
          console.log('Sync completed:', syncResult);
          
          // Wait a moment for the sync to complete
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          // Check again if data exists after sync
          const hasDataAfterSync = await apiService.checkAttendeesExist(eventDetails.eventbrite_id);
          console.log('Has attendees data after sync:', hasDataAfterSync);
        } catch (syncError) {
          console.error('Sync failed:', syncError);
          NotificationService.showError('Failed to sync attendees from Eventbrite');
        }
      }
      
      // Now get the attendees data
      const data = await apiService.getEventAttendees(eventDetails.eventbrite_id);
      setAttendeeData(data);
    } catch (error) {
      console.error('Failed to load attendee data:', error);
      NotificationService.showError('Failed to load attendee data');
    } finally {
      setAttendeeLoading(false);
    }
  };

  const handleAttendeeSync = async () => {
    if (!eventDetails?.eventbrite_id || !userToken) return;
    
    try {
      const apiService = new EventbriteApiService(userToken);
      await apiService.syncEventAttendees(eventDetails.eventbrite_id);
      await loadAttendeeData(); // Reload data after sync
      NotificationService.showSuccess('Attendees synced successfully with Eventbrite');
    } catch (error) {
      console.error('Failed to sync attendees:', error);
      NotificationService.showError('Failed to sync with Eventbrite');
    }
  };

  const handleAttendeeCheckin = async (orderId, attendee, newCheckinStatus) => {
    if (!eventDetails?.eventbrite_id || !userToken) return;
    
    try {
      const apiService = new EventbriteApiService(userToken);
      await apiService.updateAttendeeCheckin(eventDetails.eventbrite_id, orderId, newCheckinStatus);
      await loadAttendeeData(); // Reload data after check-in
      
      const action = newCheckinStatus ? 'checked in' : 'checked out';
      NotificationService.showSuccess(`${attendee.attendee_first_name} ${attendee.attendee_last_name} ${action} successfully`);
    } catch (error) {
      console.error('Failed to update check-in status:', error);
      NotificationService.showError('Failed to update check-in status');
    }
  };

  // Purchase functionality handlers
  const handlePurchaseClick = (artwork) => {
    setPurchasePanel({
      artwork,
      mode: 'direct' // For card reader/terminal processing
    });
  };

  // Handle payment link generation
  const handleGeneratePaymentLink = async (artwork) => {
    try {
      setIsProcessingPayment(true);
      
      const requestBody = {
        artwork_id: artwork.vc_id,
        purchase_type: 'link'
      };
      
      console.log('🔧 Sending request to edge function:', requestBody);
      console.log('🔧 Artwork VC ID:', artwork.vc_id);
      console.log('🔧 Artwork object:', artwork);
      console.log('🔧 Supabase client:', supabase);
      
      // 🔧 FIX: Use Supabase edge function with debugging
      const { data, error } = await supabase.functions.invoke('create_artwork_payment', {
        body: requestBody
      });

      console.log('🔧 Edge function response:', { data, error });

      if (error) {
        console.error('🔧 Edge function error:', error);
        throw new Error(error.message);
      }

      if (data && data.success && data.payment_url) {
        console.log('🔧 Payment link generated successfully:', data.payment_url);
        
        // Track that this artwork has had a payment link generated
        setGeneratedPaymentLinks(prev => new Set([...prev, artwork.vc_id]));
        
        // Show success snackbar without affecting scroll position
        setSnackbar({
          message: 'Payment link generated successfully! Click the refresh button to see the new buttons.',
          type: 'success'
        });
        
        // Auto-hide snackbar after 5 seconds
        setTimeout(() => setSnackbar(null), 5000);
        
      } else {
        console.error('🔧 Edge function returned error:', data);
        setSnackbar({
          message: 'Failed to generate payment link: ' + (data?.error || 'Unknown error'),
          type: 'error'
        });
        setTimeout(() => setSnackbar(null), 5000);
      }
    } catch (error) {
      console.error('Error generating payment link:', error);
      setSnackbar({
        message: 'Failed to generate payment link: ' + error.message,
        type: 'error'
      });
      setTimeout(() => setSnackbar(null), 5000);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Handle direct payment (card reader)
  const handleDirectPayment = async (artwork) => {
    try {
      setIsProcessingPayment(true);
      
      // 🔧 FIX: Use Supabase edge function instead of /api/ endpoint
      const { data, error } = await supabase.functions.invoke('create_artwork_payment', {
        body: {
          artwork_id: artwork.vc_id,
          purchase_type: 'terminal'
        }
      });

      if (error) {
        throw new Error(error.message);
      }
      
      if (data.success && data.client_secret) {
        // For now, show instructions for manual processing
        setPaymentResult({
          success: true,
          message: `Payment Intent created! Use Stripe Dashboard or Terminal to process $${artwork.price}`,
          paymentIntentId: data.payment_intent_id,
          artwork: artwork
        });
      } else {
        throw new Error(data.error || 'Failed to create payment intent');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setPaymentResult({
        success: false,
        message: 'Payment failed: ' + error.message,
        artwork: artwork
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleCopyPaymentLink = async (url) => {
    try {
      console.log('🔧 Copying payment link to clipboard:', url);
      await navigator.clipboard.writeText(url);
      setCopiedPaymentLink(true);
      setCopiedPaymentLinks(prev => new Set([...prev, url]));
      setTimeout(() => {
        setCopiedPaymentLink(false);
        setCopiedPaymentLinks(prev => {
          const newSet = new Set(prev);
          newSet.delete(url);
          return newSet;
        });
      }, 2000);
      console.log('✅ Payment link copied successfully');
    } catch (error) {
      console.error('❌ Failed to copy link:', error);
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopiedPaymentLink(true);
        setCopiedPaymentLinks(prev => new Set([...prev, url]));
        setTimeout(() => {
          setCopiedPaymentLink(false);
          setCopiedPaymentLinks(prev => {
            const newSet = new Set(prev);
            newSet.delete(url);
            return newSet;
          });
        }, 2000);
        console.log('✅ Payment link copied using fallback method');
      } catch (fallbackError) {
        console.error('❌ Fallback copy method also failed:', fallbackError);
        alert('Unable to copy to clipboard. Please manually copy the link.');
      }
    }
  };

  const handleCopyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmails(prev => new Set([...prev, email]));
      setTimeout(() => setCopiedEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(email);
        return newSet;
      }), 2000);
    } catch (error) {
      console.error('Failed to copy email:', error);
    }
  };

  const generateQRCode = async (url) => {
    try {
      console.log('🔧 Generating QR code for URL:', url);
      const qrDataUrl = await QRCode.toDataURL(url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      console.log('🔧 QR code generated successfully:', qrDataUrl.substring(0, 50) + '...');
      setQrCodeUrl(qrDataUrl);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    }
  };

  // Quick Story Creation


  // Quality Badge Component
  const QualityBadge = ({ status }) => {
      const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'high':
        return { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'High Quality' };
      case 'medium':
        return { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Medium Quality' };
      case 'low':
        return { color: 'bg-red-100 text-red-800', icon: AlertCircle, text: 'Low Quality' };
      default:
        return null;
    }
  };

    const config = getStatusConfig(status);
    
    if (!config) {
      return null;
    }
    
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <IconComponent className="w-3 h-3" />
        {config.text}
      </span>
    );
  };

  // Artwork Card Component
  const ArtworkCard = ({ artwork }) => {
    // Debug payment link
    console.log('🔧 Artwork:', artwork.title, 'has payment_link:', !!artwork.payment_link, artwork.payment_link);
    
    const isCheckedIn = eventDetails && artwork.location === eventDetails.gallery_id;
    const canCheckIn = eventDetails && artwork.accepted;
    
    const handleCheckInToggle = (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (!canCheckIn || checkInLoading) return;
      
      // Save scroll position before state change
      const scrollY = window.scrollY;
      
      const restoreScroll = () => {
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
      };
      
      if (isCheckedIn) {
        handleUnCheckInArtwork(artwork);
      } else {
        handleCheckInArtwork(artwork);
      }
      
      // Restore scroll after state update
      restoreScroll();
    };
    
    return (
    <div className="artwork-card-new">
      {/* Image Container - Larger, cleaner */}
      <div className="artwork-image-container" onClick={() => setSelectedArtwork(artwork)}>
        <img src={getCloudinaryUrl(artwork.cover_image, { width: 400, height: 400 })} alt={artwork.title} className="artwork-image" />
        
        {/* Minimal overlay badges */}
        {artwork.vc_id && (
          <div className="order-number">#{artwork.vc_id}</div>
        )}
        
        {/* Clickable Check-in Toggle (when event exists and artwork accepted) */}
        {canCheckIn ? (
          <button
            onClick={handleCheckInToggle}
            disabled={checkInLoading}
            title={isCheckedIn ? 'Click to check out' : 'Click to check in'}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: '2px solid white',
              backgroundColor: isCheckedIn ? '#16a34a' : '#f97316',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: checkInLoading ? 'wait' : 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              transition: 'all 0.2s ease',
              padding: 0,
              zIndex: 10,
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {isCheckedIn ? '✓' : '○'}
          </button>
        ) : (
          <div className={`status-indicator ${artwork.accepted ? 'accepted' : 'submitted'}`}>
            {artwork.accepted ? '✓' : '○'}
          </div>
        )}
      </div>
      
      {/* Compact Info Section */}
      <div className="artwork-card-info">
        <div className="artwork-header">
          <h4 className="artwork-title">{artwork.title}</h4>
          <p className="artwork-artist">
            {artwork.artist.nickname || `${artwork.artist.first_name} ${artwork.artist.last_name}`}
          </p>
        </div>
        
        {/* Quick Stats Row */}
        <div className="artwork-stats-row">
          {artwork.width && artwork.height && (
            <span className="dimensions">{artwork.width}"×{artwork.height}"</span>
          )}
          {artwork.price && (
            <span className="price">${artwork.price.toLocaleString()}</span>
          )}
          {artwork.sold && <span className="sold-badge">SOLD</span>}
        </div>
      </div>
      
      {/* Action Buttons Row - Clean, horizontal layout */}
      <div className="artwork-actions-row">
        {/* Acceptance Toggle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleAcceptanceStatus(artwork);
          }}
          className={`action-btn acceptance-btn ${artwork.accepted ? 'accepted' : 'submitted'}`}
          title={artwork.accepted ? 'Click to reject' : 'Click to accept'}
        >
          {artwork.accepted ? '✓ Accepted' : '○ Review'}
        </button>
        
        {/* Purchase Actions - Only show if has price and not sold */}
        {artwork.price && !artwork.sold && (
          <div className="purchase-actions-section">
            {/* Show payment link info if it exists */}
            {artwork.payment_link ? (
              <div className="payment-link-actions" style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyPaymentLink(artwork.payment_link);
                  }}
                  className="action-btn copy-link-btn"
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  title={copiedPaymentLinks.has(artwork.payment_link) ? 'Copied!' : 'Copy payment link'}
                >
                  {copiedPaymentLinks.has(artwork.payment_link) ? <Check size={12} /> : <Copy size={12} />}
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(artwork.payment_link, '_blank');
                  }}
                  className="action-btn view-link-btn"
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  title="Open payment link"
                >
                  <ExternalLinkIcon size={12} />
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    generateQRCode(artwork.payment_link);
                  }}
                  className="action-btn qr-btn"
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  title="Generate QR code"
                >
                  <QrCode size={12} />
                </button>
                <button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleGeneratePaymentLink(artwork);
                  }}
                  className="action-btn regenerate-btn"
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  title="Regenerate payment link"
                >
                  <RefreshCw size={12} />
                </button>
              </div>
            ) : (
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleGeneratePaymentLink(artwork);
                }}
                className="action-btn purchase-btn"
                disabled={isProcessingPayment || generatedPaymentLinks.has(artwork.vc_id)}
                title={
                  generatedPaymentLinks.has(artwork.vc_id) 
                    ? 'Payment link generated - refresh page to see buttons'
                    : `Generate payment link for $${artwork.price.toLocaleString()}`
                }
              >
                <ShoppingCart size={14} />
                {generatedPaymentLinks.has(artwork.vc_id) 
                  ? 'Link Generated - Refresh Page'
                  : isProcessingPayment 
                    ? 'Generating...' 
                    : 'Generate Payment Link'
                }
              </button>
            )}
          </div>
        )}
        
        {/* Check-In Action - Only show if event exists */}
        {eventDetails && (
          <button 
            onClick={(e) => {
              e.stopPropagation();
              if (artwork.location !== eventDetails.gallery_id) {
                handleCheckInArtwork(artwork);
              }
            }}
            disabled={checkInLoading || artwork.location === eventDetails.gallery_id}
            className={`action-btn checkin-btn ${artwork.location === eventDetails.gallery_id ? 'checked-in' : ''}`}
            title={artwork.location === eventDetails.gallery_id ? 'Already checked in' : 'Check in artwork'}
          >
            {artwork.location === eventDetails.gallery_id ? '✓ Checked In' : '📍 Check In'}
          </button>
        )}
      </div>
    </div>
  );
};

  // Compact Artwork Card Component - For Check-in Tab
  // Large tack-format image with natural aspect ratio
  const CompactArtworkCard = ({ artwork }) => {
    const isCheckedIn = eventDetails && artwork.location === eventDetails.gallery_id;
    const artistName = artwork.artist?.nickname || `${artwork.artist?.first_name || ''} ${artwork.artist?.last_name || ''}`.trim();
    
    // Get aspect-ratio preserving thumbnail URL
    const getAspectRatioUrl = (url) => {
      if (!url) return '';
      if (url.includes('cloudinary.com')) {
        return url.replace('/upload/', '/upload/t_fitmax-300/');
      }
      return url;
    };

    const handleCheckInToggle = (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (checkInLoading) return;
      
      // Save scroll position before state change
      const scrollY = window.scrollY;
      
      const restoreScroll = () => {
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
      };
      
      if (isCheckedIn) {
        handleUnCheckInArtwork(artwork);
      } else {
        handleCheckInArtwork(artwork);
      }
      
      // Restore scroll after state update
      restoreScroll();
    };

    return (
      <div 
        style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          cursor: 'pointer'
        }}
        onClick={() => setSelectedArtwork(artwork)}
      >
        {/* Top row: Image + Info + Button (horizontal layout) */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}>
          {/* Large thumbnail - natural aspect ratio (tack format) */}
          <div style={{ 
            position: 'relative',
            width: '100px',
            flexShrink: 0,
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#f3f4f6'
          }}>
            <img 
              src={getAspectRatioUrl(artwork.cover_image)} 
              alt={artwork.title}
              style={{ 
                width: '100%', 
                height: 'auto',
                display: 'block',
                borderRadius: '8px'
              }}
            />
            {/* Check-in indicator overlay */}
            {isCheckedIn && (
              <div style={{
                position: 'absolute',
                bottom: '4px',
                right: '4px',
                width: '20px',
                height: '20px',
                background: '#16a34a',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}>
                <CheckCircle size={12} color="white" />
              </div>
            )}
          </div>

          {/* Info section - takes remaining space */}
          <div style={{ 
            flex: 1, 
            minWidth: 0, 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center',
            gap: '6px'
          }}>
            <h4 style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#111827',
              margin: 0,
              lineHeight: '1.3',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {artwork.title}
            </h4>
            
            <p style={{
              fontSize: '0.8rem',
              color: '#6b7280',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {artistName}
            </p>

            {/* Status badges inline */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                fontSize: '0.7rem',
                fontWeight: '500',
                padding: '2px 6px',
                borderRadius: '4px',
                background: isCheckedIn ? '#dcfce7' : '#fee2e2',
                color: isCheckedIn ? '#166534' : '#dc2626',
                whiteSpace: 'nowrap'
              }}>
                {isCheckedIn ? '✓ In' : '○ Pending'}
              </span>
              {artwork.price && (
                <span style={{
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  color: '#1e40af',
                  padding: '2px 6px',
                  background: '#dbeafe',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap'
                }}>
                  ${artwork.price.toLocaleString()}
                </span>
              )}
            </div>
          </div>

          {/* Check-in toggle button - fixed width on right */}
          {eventDetails && (
            <button 
              onClick={handleCheckInToggle}
              disabled={checkInLoading}
              style={{
                flexShrink: 0,
                padding: '8px 12px',
                backgroundColor: isCheckedIn ? '#16a34a' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: checkInLoading ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                minWidth: '80px',
                alignSelf: 'center',
                opacity: checkInLoading ? 0.6 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              <CheckCircle size={14} />
              <span>{isCheckedIn ? 'Check Out' : 'Check In'}</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  // Artist Card Component - Simplified
  const ArtistCard = ({ artist }) => {
    // Debug: Log artist data
    console.log('ArtistCard rendering for:', {
      uuid: artist.uuid,
      nickname: artist.nickname,
      first_name: artist.first_name,
      last_name: artist.last_name,
      email: artist.email, // Add email to debug output
      artwork_count: artist.artwork_count_in_collection,
      joined: artist.joined_collection
    });
    
    // Get artworks for this artist
    const artistArtworks = artworks.filter(artwork => artwork.artist?.uuid === artist.uuid);
    
    return (
      <div className="artist-card" style={{background: 'white', padding: '1rem'}}>
        {/* Artist names should always be visible - AGGRESSIVE FIX */}
        <div className="artist-header" style={{display: 'flex', alignItems: 'center', marginBottom: '0.75rem'}}>
          <h4 
            className="artist-name" 
            style={{
              opacity: '1 !important', 
              visibility: 'visible !important', 
              display: 'block !important',
              color: '#111827 !important',
              fontSize: '1.125rem',
              fontWeight: '600',
              margin: '0',
              lineHeight: '1.3'
            }}
          >
            {artist.nickname || `${artist.first_name} ${artist.last_name}`}
          </h4>
        </div>
        
        <div className="artist-stats">
          <span style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '0.25rem', 
            backgroundColor: '#e0e7ff', 
            color: '#4338ca', 
            fontSize: '0.75rem', 
            fontWeight: '500', 
            padding: '0.25rem 0.5rem', 
            borderRadius: '0.25rem' 
          }}>
            <BookOpen style={{ width: '0.75rem', height: '0.75rem' }} />
            {artist.artwork_count_in_collection} artworks
          </span>
          <span style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '0.25rem', 
            backgroundColor: '#dcfce7', 
            color: '#166534', 
            fontSize: '0.75rem', 
            fontWeight: '500', 
            padding: '0.25rem 0.5rem', 
            borderRadius: '0.25rem' 
          }}>
            <CheckCircle style={{ width: '0.75rem', height: '0.75rem' }} />
            {artistArtworks.filter(a => a.accepted).length} accepted
          </span>
          <span style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '0.25rem', 
            backgroundColor: '#fef3c7', 
            color: '#d97706', 
            fontSize: '0.75rem', 
            fontWeight: '500', 
            padding: '0.25rem 0.5rem', 
            borderRadius: '0.25rem' 
          }}>
            <Calendar style={{ width: '0.75rem', height: '0.75rem' }} />
            Joined {new Date(artist.joined_collection).toLocaleDateString()}
          </span>
        </div>

        {/* Communication Status */}
        <div className="artist-communication-status" style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {artist.has_app && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', backgroundColor: '#dcfce7', color: '#166534', fontSize: '0.75rem', fontWeight: '500', padding: '0.25rem 0.5rem', borderRadius: '0.25rem' }}>
              <Smartphone style={{ width: '0.75rem', height: '0.75rem' }} />
              Has App
            </span>
          )}
          {artist.has_paid && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', backgroundColor: '#dbeafe', color: '#1e40af', fontSize: '0.75rem', fontWeight: '500', padding: '0.25rem 0.5rem', borderRadius: '0.25rem' }}>
              <CheckCircle style={{ width: '0.75rem', height: '0.75rem' }} />
              Paid
            </span>
          )}
          {artist.has_submitted_artwork && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', backgroundColor: '#fef3c7', color: '#d97706', fontSize: '0.75rem', fontWeight: '500', padding: '0.25rem 0.5rem', borderRadius: '0.25rem' }}>
              <ImageIcon style={{ width: '0.75rem', height: '0.75rem' }} />
              Submitted
            </span>
          )}
        </div>
        
        {/* Artist Email - Debug Version */}
        <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f0f9ff', borderRadius: '0.5rem', fontSize: '0.75rem' }}>
          <strong>Email Debug:</strong> {artist.email ? artist.email : 'No email found'}
        </div>
        
        {/* Artist Email */}
        {artist.email && (
          <div className="artist-email" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#f8fafc', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
            <Mail style={{ width: '1rem', height: '1rem', color: '#64748b', flexShrink: 0 }} />
            <span style={{ fontSize: '0.875rem', color: '#334155', fontFamily: 'monospace', flexGrow: 1, minWidth: 0, wordBreak: 'break-all' }}>
              {artist.email}
            </span>
            <button
              onClick={() => handleCopyEmail(artist.email)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '2rem',
                height: '2rem',
                backgroundColor: copiedEmails.has(artist.email) ? '#dcfce7' : '#f1f5f9',
                color: copiedEmails.has(artist.email) ? '#166534' : '#475569',
                border: '1px solid',
                borderColor: copiedEmails.has(artist.email) ? '#bbf7d0' : '#cbd5e1',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                flexShrink: 0
              }}
              title={copiedEmails.has(artist.email) ? 'Copied!' : 'Copy email to clipboard'}
              onMouseEnter={(e) => {
                if (!copiedEmails.has(artist.email)) {
                  e.target.style.backgroundColor = '#e2e8f0';
                  e.target.style.borderColor = '#94a3b8';
                }
              }}
              onMouseLeave={(e) => {
                if (!copiedEmails.has(artist.email)) {
                  e.target.style.backgroundColor = '#f1f5f9';
                  e.target.style.borderColor = '#cbd5e1';
                }
              }}
            >
              {copiedEmails.has(artist.email) ? 
                <CheckIcon style={{ width: '0.875rem', height: '0.875rem' }} /> : 
                <Copy style={{ width: '0.875rem', height: '0.875rem' }} />
              }
            </button>
          </div>
        )}
        {/* Debug: Show if email is missing */}
        {!artist.email && (
          <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#fee2e2', borderRadius: '0.5rem', fontSize: '0.75rem', color: '#991b1b' }}>
            No email found for this artist
          </div>
        )}

      </div>
    );
  };

  // Purchase Panel Component
  const PurchasePanel = () => {
    if (!purchasePanel) return null;

    const { artwork, mode, paymentUrl } = purchasePanel;

    return (
      <div className="purchase-modal" onClick={() => setPurchasePanel(null)}>
        <div className="purchase-modal-content" onClick={e => e.stopPropagation()}>
          <div className="purchase-modal-header">
            <h3>Process Sale</h3>
            <button onClick={() => setPurchasePanel(null)}>
              <X />
            </button>
          </div>

          <div className="purchase-modal-body">
            {/* Artwork Info */}
            <div className="purchase-artwork-info">
              <img src={getCloudinaryUrl(artwork.cover_image, { width: 500, height: 500 })} alt={artwork.title} className="purchase-artwork-image" />
              <div>
                <h4>{artwork.title}</h4>
                <p className="artist-name">
                  by {artwork.artist.nickname || `${artwork.artist.first_name} ${artwork.artist.last_name}`}
                </p>
                <p className="price-display">${artwork.price.toLocaleString()}</p>
              </div>
            </div>

            {mode === 'direct' && (
              <div className="direct-payment-section">
                <h4>Card Reader Payment</h4>
                <p>Connect your Stripe card reader to process this payment directly.</p>
                
                <div className="payment-actions">
                  <button 
                    onClick={() => handleDirectPayment(artwork)}
                    disabled={isProcessingPayment}
                    className="payment-btn primary"
                  >
                    {isProcessingPayment ? (
                      <>Processing...</>
                    ) : (
                      <>
                        <CreditCard />
                        Process Card Payment
                      </>
                    )}
                  </button>
                  
                  <button 
                    onClick={() => setPurchasePanel({ ...purchasePanel, mode: 'link' })}
                    className="payment-btn secondary"
                  >
                    <ExternalLinkIcon />
                    Generate Payment Link Instead
                  </button>
                </div>
              </div>
            )}

            {mode === 'link' && (
              <div className="payment-link-section">
                <h4>Payment Link</h4>
                <p>Share this link with the customer or display as QR code:</p>
                
                {paymentUrl ? (
                  <div className="payment-link-display">
                    <input 
                      type="text" 
                      value={paymentUrl} 
                      readOnly 
                      className="payment-link-input"
                    />
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCopyPaymentLink(paymentUrl);
                      }}
                      className="copy-btn"
                      title={copiedPaymentLink ? 'Copied!' : 'Copy payment link to clipboard'}
                    >
                      {copiedPaymentLink ? <CheckIcon /> : <Copy />}
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={() => handleGeneratePaymentLink(artwork)}
                    disabled={isProcessingPayment}
                    className="payment-btn primary"
                  >
                    {isProcessingPayment ? 'Generating...' : 'Generate Payment Link'}
                  </button>
                )}

                {/* QR Code Display */}
                {console.log('🔧 QR Code Debug - qrCodeUrl:', qrCodeUrl ? 'exists' : 'null', 'paymentUrl:', paymentUrl ? 'exists' : 'null')}
                {qrCodeUrl && paymentUrl && (
                  <div className="qr-code-section">
                    <h5>QR Code</h5>
                    <div className="qr-code-container">
                      <img src={qrCodeUrl} alt="Payment QR Code" className="qr-code-image" />
                      <div className="qr-code-actions">
                        <button 
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = qrCodeUrl;
                            link.download = `payment-qr-${artwork.vc_id}.png`;
                            link.click();
                          }}
                          className="qr-download-btn"
                        >
                          <Download className="btn-icon" />
                          Download QR
                        </button>
                        <button 
                          onClick={() => {
                            const printWindow = window.open('', '_blank');
                            printWindow.document.write(`
                              <html>
                                <head><title>Payment QR Code</title></head>
                                <body style="margin:0;padding:20px;text-align:center;">
                                  <h2>${artwork.title} - Payment QR Code</h2>
                                  <img src="${qrCodeUrl}" style="max-width:300px;height:auto;" />
                                  <p>Scan to pay: $${artwork.price}</p>
                                </body>
                              </html>
                            `);
                            printWindow.document.close();
                            printWindow.print();
                          }}
                          className="qr-print-btn"
                        >
                          <Printer className="btn-icon" />
                          Print QR
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="payment-link-actions">
                  <button 
                    onClick={() => window.open(paymentUrl, '_blank')}
                    className="payment-btn secondary"
                    disabled={!paymentUrl}
                  >
                    <ExternalLinkIcon />
                    Open in New Tab
                  </button>
                </div>
              </div>
            )}

            {/* Payment Result */}
            {paymentResult && (
              <div className={`payment-result ${paymentResult.success ? 'success' : 'error'}`}>
                <p>{paymentResult.message}</p>
                {paymentResult.success && (
                  <button 
                    onClick={() => {
                      setPurchasePanel(null);
                      setPaymentResult(null);
                    }}
                    className="payment-btn primary"
                  >
                    Complete Sale
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Load voting data
  const loadVotingData = async () => {
    if (!collectionId) return;
    
    try {
      setVotingLoading(true);
      
      // Direct SQL call using Supabase RPC
      const { data, error: rpcError } = await supabase.rpc('get_collection_voting_admin_summary', {
        collection_id_param: collectionId
      });

      if (rpcError) {
        console.error('Error loading voting data:', rpcError);
        setVotingData(null);
      } else {
        setVotingData(data);
      }
    } catch (err) {
      console.error('Error loading voting data:', err);
      setVotingData(null);
    } finally {
      setVotingLoading(false);
    }
  };

  // Load judged totals per artwork for Artists tab (prefers active round, otherwise latest round)
  const loadJudgingTotalsData = async () => {
    if (!collectionId || artworks.length === 0) {
      setJudgingTotalsByVcId({});
      return;
    }

    try {
      const { data: rounds, error: roundsError } = await supabase
        .from('collection_judging_rounds')
        .select('id, is_active, created_at')
        .eq('collection_id', collectionId)
        .order('is_active', { ascending: false })
        .order('created_at', { ascending: false });

      if (roundsError) {
        console.error('Error loading judging rounds for totals:', roundsError);
        setJudgingTotalsByVcId({});
        return;
      }

      const targetRound = rounds?.[0];
      if (!targetRound) {
        setJudgingTotalsByVcId({});
        return;
      }

      // Prefer raw score rows so totals include unaccepted artworks too.
      const rawScoreSources = [
        { table: 'collection_judge_scores', roundColumn: 'round_id' },
        { table: 'collection_judge_scores', roundColumn: 'judging_round_id' },
        { table: 'collection_judging_scores', roundColumn: 'round_id' },
        { table: 'collection_judging_scores', roundColumn: 'judging_round_id' },
        { table: 'judge_scores', roundColumn: 'round_id' },
        { table: 'judge_scores', roundColumn: 'judging_round_id' },
        { table: 'judging_scores', roundColumn: 'round_id' },
        { table: 'judging_scores', roundColumn: 'judging_round_id' }
      ];

      for (const source of rawScoreSources) {
        try {
          const { data: scoreRows, error: scoreError } = await supabase
            .from(source.table)
            .select('vc_id, score')
            .eq(source.roundColumn, targetRound.id);

          if (scoreError) {
            continue;
          }

          if (scoreRows && scoreRows.length > 0) {
            const totalsMap = {};
            scoreRows.forEach((row) => {
              if (row?.vc_id == null) return;
              const numericScore = Number(row?.score);
              if (!Number.isFinite(numericScore)) return;
              totalsMap[row.vc_id] = (totalsMap[row.vc_id] || 0) + numericScore;
            });

            setJudgingTotalsByVcId(totalsMap);
            return;
          }
        } catch (_rawScoreErr) {
          // Try next candidate table/column combination.
        }
      }

      const { data: result, error: rpcError } = await supabase.rpc('get_judging_results', {
        round_id_param: targetRound.id
      });

      if (rpcError) {
        console.error('Error loading judging totals:', rpcError);
        setJudgingTotalsByVcId({});
        return;
      }

      if (result?.error) {
        console.error('Judging totals RPC returned error:', result.error);
        setJudgingTotalsByVcId({});
        return;
      }

      const ranked = result?.artworks_ranked || [];
      const totalsMap = {};

      ranked.forEach((artwork) => {
        let total = null;

        if (artwork?.total_score != null) total = artwork.total_score;
        else if (artwork?.aggregate_score != null) total = artwork.aggregate_score;
        else if (artwork?.sum_score != null) total = artwork.sum_score;
        else {
          const avg = Number(artwork?.average_score);
          const votes = Number(artwork?.score_count);
          if (Number.isFinite(avg) && Number.isFinite(votes)) {
            total = Number((avg * votes).toFixed(2));
          }
        }

        if (total != null && artwork?.vc_id != null) {
          totalsMap[artwork.vc_id] = total;
        }
      });

      setJudgingTotalsByVcId(totalsMap);
    } catch (err) {
      console.error('Error loading judging totals data:', err);
      setJudgingTotalsByVcId({});
    }
  };

  // Load voting data when collection loads
  useEffect(() => {
    if (collection) {
      loadVotingData();
    }
  }, [collection]);

  // Load judged totals for Artists tab after artworks are loaded
  useEffect(() => {
    loadJudgingTotalsData();
  }, [collectionId, artworks.length]);

  // Reset check-in filter when switching to check-in tab
  useEffect(() => {
    if (activeTab === 'checkin') {
      setCheckInFilter('all');
      setCheckInSearch('');
      setCheckInArtistFilter('all');
    }
  }, [activeTab]);

  // Get available tabs based on data
  /**
   * Determine collection state for conditional UI rendering
   * STATE 1: Empty (no event AND no artworks) - show full onboarding flow with all 4 steps
   * STATE 2: Event Only (has event but no artworks) - show simplified submission panel
   * STATE 3: Has Artworks (artworks exist) - show full tabs
   */
  const getCollectionState = () => {
    const hasArtworks = artworks.length > 0;
    const hasEvent = !!eventDetails;
    
    if (hasArtworks) return 'has-artworks';
    if (hasEvent) return 'event-only';  // Has event but no artworks yet
    return 'empty';  // Truly empty - no event and no artworks
  };

  const collectionState = getCollectionState();

  const getAvailableTabs = () => {
    // No artworks yet - but if a submission exists, show the Call Outs tab
    if (collectionState === 'empty' || collectionState === 'event-only') {
      if (submissionDetails) {
        const tabs = [];
        if (collectionEvents.length > 0) {
          tabs.push({ 
            id: 'events', 
            label: `Events (${collectionEvents.length})`, 
            icon: Calendar 
          });
        }
        tabs.push({ id: 'submissions', label: 'Call Outs', icon: FileText });
        return tabs;
      }
      return [];
    }

    // Has artworks - show full tabs
    const tabs = [
      { id: 'artworks', label: `Artworks (${artworks.length})`, icon: ImageIcon },
      { id: 'artists', label: `Artists (${artists.length})`, icon: Users },
      { id: 'acceptances', label: 'Acceptances', icon: CheckCircle },
    ];

    // Add check-in tab if event exists
    if (eventDetails) {
      const acceptedArtworks = artworks.filter(art => art.accepted === true);
      const checkedInCount = acceptedArtworks.filter(art => art.location === eventDetails.gallery_id).length;
      tabs.push({ 
        id: 'checkin', 
        label: `Check-In (${checkedInCount}/${acceptedArtworks.length})`, 
        icon: CheckCircle 
      });
      // Add scheduling tab right after check-in (for drop-off/pick-up scheduling)
      tabs.push({ 
        id: 'scheduling', 
        label: 'Drop Off Scheduling', 
        icon: CalendarClock,
        isLink: true,
        href: `/curator/collections/${collectionId}/scheduling`
      });
    }

    // Events tab - always show so users can create events even if none exist yet
    tabs.push({ 
      id: 'events', 
      label: collectionEvents.length > 0 ? `Events (${collectionEvents.length})` : 'Events', 
      icon: Calendar 
    });

    // Legacy 'event' tab removed — attendee management is now handled in EventDetailAdmin

    // Add submissions tab (always show to allow creation/management)
    tabs.push({ id: 'submissions', label: 'Call Outs', icon: FileText });

    // Always show voting tab so admins can create a voting period if none exists
    tabs.push({
      id: 'voting',
      label: votingData?.has_voting
        ? `Voting (${votingData.voting_statistics?.total_unique_voters || 0})`
        : 'Voting',
      icon: Vote
    });

    // Add judging tab (always available for collections with artworks)
    tabs.push({ id: 'judging', label: 'Judging', icon: Award });

    // Add prints tab if collection has a print campaign
    if (hasPrintCampaign) {
      tabs.push({ id: 'prints', label: 'Prints', icon: Printer });
    }

    // Financial summary — always show for collections with events or submission fees
    tabs.push({ id: 'financials', label: 'Financials', icon: BarChart3 });

    return tabs;
  };

  // Placeholder to maintain compatibility - will be removed
  const _unusedEventOnlyTabs = () => {
    const tabs = [
      { id: 'submissions', label: 'Submissions', icon: FileText },
      { id: 'communications', label: 'Communications', icon: Send },
        { id: 'inquiries', label: `Collection Activity (${collectorInquiries.length})`, icon: Bell },
        { id: 'acceptances', label: 'Acceptances', icon: CheckCircle },
        { id: 'event', label: 'Event & Attendees', icon: Calendar }
      ];
      return tabs;
    return [];
  };

  // Reset active tab if it becomes unavailable
  const availableTabs = getAvailableTabs();
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(tab => tab.id === activeTab)) {
      setActiveTab(availableTabs[0].id);
    }
  }, [collectionState, availableTabs]);

  if (loading) {
    return (
      <div className="collection-detail-admin">
        <div className="loading-state">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <h3>Loading Collection...</h3>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="collection-detail-admin">
        <div className="error-state">
          <AlertCircle className="error-icon" />
          <h3>Error Loading Collection</h3>
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="btn-secondary">
            <ArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="collection-detail-admin">
        <div className="error-state">
          <AlertCircle className="error-icon" />
          <h3>Collection Not Found</h3>
          <p>The requested collection could not be found.</p>
          <button onClick={() => navigate(-1)} className="btn-secondary">
            <ArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="collection-detail-admin">
      {/* Container Overflow Fix - Updated Layout v3 - Aggressive Viewport Constraint */}
      {/* Header - Only show when there's no event (event banner replaces this) */}
      {!eventDetails && (
        <div className="detail-header">
          <div className="detail-header-content">
            <div className="detail-header-left">
              <div className="detail-title-section">
                <div className="detail-meta">
                  <span className="collection-type">{collection.type || 'Collection'}</span>
                  {collection.quality_status && collection.quality_status !== 'unknown' && (
                    <QualityBadge status={collection.quality_status} />
                  )}
                </div>
                <h1 className="detail-title">{collection.title}</h1>
                {renderDescription(collection.description)}
              </div>
            </div>
            
            <div className="detail-header-actions">
              {/* Curator Tools Dropdown */}
              <CuratorToolsDropdown
                artworkCount={artworks.length}
                onView={() => window.open(`/collection/${collectionId}`, '_blank')}
                onEdit={() => navigate(`/curator/collections/${collectionId}/edit`)}
                onDisplayCards={goToPdfGenerator}
                onWallPlanner={goToWallPlanner}
                onGalleryExperience={goTo3DEditor}
              />
            </div>
          </div>
        </div>
      )}

      {/* Notification messages (show regardless of event) */}
      {/* Metadata Update Result Message */}
      {metadataUpdateResult && (
        <div className={`distribution-result ${metadataUpdateResult.success ? 'success' : 'error'}`}>
          <div className="result-content">
            {metadataUpdateResult.success ? (
              <CheckCircle className="result-icon" />
            ) : (
              <AlertCircle className="result-icon" />
            )}
            <span>{metadataUpdateResult.message}</span>
          </div>
        </div>
      )}

      {/* Check-in Notification Message */}
      {notification && (
        <div className={`distribution-result ${notification.type === 'success' ? 'success' : 'error'}`}>
          <div className="result-content">
            {notification.type === 'success' ? (
              <CheckCircle className="result-icon" />
            ) : (
              <AlertCircle className="result-icon" />
            )}
            <span>{notification.message}</span>
          </div>
        </div>
      )}

      {/* Event Header Banner - Shows when event exists */}
      {eventDetails && (
        <EventHeaderBanner
          eventDetails={eventDetails}
          artworkCount={artworks.length}
          artistCount={artists.length}
          checkInStats={{
            total: artworks.length,
            checkedIn: artworks.filter(art => art.location === eventDetails?.gallery_id).length
          }}
          curatorToolsProps={{
            artworkCount: artworks.length,
            onView: () => window.open(`/collection/${collectionId}`, '_blank'),
            onEdit: () => navigate(`/curator/collections/${collectionId}/edit`),
            onDisplayCards: goToPdfGenerator,
            onWallPlanner: goToWallPlanner,
            onGalleryExperience: goTo3DEditor
          }}
          artworks={artworks}
        />
      )}

      {/* Get Started Workflow - Shows only when truly empty (no event AND no artworks) */}
      {collectionState === 'empty' && (
        <GetStartedWorkflow
          onEditCollection={() => {
            // Navigate to edit collection page
            navigate(`/curator/collections/${collection.collection_id}/edit`);
          }}
          onAddExhibition={() => {
            // Open event creation modal or navigate to event tab
            setActiveTab('event');
          }}
          onCreateSubmission={() => {
            // Navigate to create submission
            navigate(`/curator/submissions/new?collection_id=${collection.collection_id}`);
          }}
          onAcceptArtworks={() => {
            // This would only be available once they have artworks
            // For now, explain they need to complete earlier steps
            alert('Complete the first 3 steps to start curating artworks');
          }}
          onAddArtwork={() => {
            // Navigate to artworks page with pre-selected collection
            navigate(`/curator/artworks?addToCollection=${collection.collection_id}`);
          }}
        />
      )}

      {/* Simplified Add Artworks Panel - Shows when has event but no artworks yet */}
      {collectionState === 'event-only' && (
        <div className="add-artworks-panel" style={{
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '0.75rem',
          padding: '2rem',
          margin: '0 0 1.5rem 0',
          textAlign: 'center'
        }}>
          <ImageIcon size={48} style={{ color: '#94a3b8', marginBottom: '1rem' }} />
          <h3 style={{ 
            fontSize: '1.25rem', 
            fontWeight: '600', 
            color: '#1e293b',
            margin: '0 0 0.5rem 0'
          }}>
            No Artworks Yet
          </h3>
          <p style={{ 
            fontSize: '0.9rem', 
            color: '#64748b',
            margin: '0 0 1.5rem 0',
            maxWidth: '500px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            {submissionDetails 
              ? "Artists will submit their work once the submission form is open."
              : "Create a submission form for artists, or browse and add artworks directly."}
          </p>
          
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            {!submissionDetails && (
              <button
                onClick={() => navigate(`/curator/submissions/new?collection_id=${collection.collection_id}`)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <FileText size={18} />
                Create Submission Form
              </button>
            )}
            {submissionDetails && (
              <button
                onClick={() => navigate(`/curator/collections/${collection.collection_id}/edit-submission`)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <FileText size={18} />
                Edit Submission Form
              </button>
            )}
            <button
              onClick={() => navigate(`/curator/artworks?addToCollection=${collection.collection_id}`)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#0ea5e9',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <ImageIcon size={18} />
              Browse & Add Artwork
            </button>
          </div>
        </div>
      )}

      {/* Dynamic Tabs - Show if collection has artworks OR has a linked submission */}
      {(collectionState === 'has-artworks' || (submissionDetails && availableTabs.length > 0)) && (
        <div className="detail-tabs">
          {availableTabs.map((tab) => {
            const IconComponent = tab.icon;
            // Special handling for tabs that link to standalone pages (prints, scheduling)
            if (tab.isLink || tab.id === 'prints') {
              const href = tab.href || `/curator/collections/${collectionId}/${tab.id}`;
              return (
                <button
                  key={tab.id}
                  onClick={() => navigate(href)}
                  className="tab-button prints-link"
                  title={`Open ${tab.label}`}
                >
                  <IconComponent className="tab-icon" />
                  {tab.label}
                  <ExternalLink size={12} style={{ marginLeft: 4, opacity: 0.6 }} />
                </button>
              );
            }
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              >
                <IconComponent className="tab-icon" />
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Tab Content - Show if collection has artworks OR has a linked submission */}
      {(collectionState === 'has-artworks' || (submissionDetails && availableTabs.length > 0)) && (
      <div className="detail-content" style={{maxWidth: '100vw', overflowX: 'hidden'}}>
        {activeTab === 'artworks' && (
          <ArtworksTab
            artworks={artworks}
            filteredArtworks={filteredArtworks}
            artworkFilter={artworkFilter}
            setArtworkFilter={setArtworkFilter}
            artworkSort={artworkSort}
            setArtworkSort={setArtworkSort}
            eventDetails={eventDetails}
            isProcessingPayment={isProcessingPayment}
            generatedPaymentLinks={generatedPaymentLinks}
            copiedPaymentLinks={copiedPaymentLinks}
            toggleAcceptanceStatus={toggleAcceptanceStatus}
            handleGeneratePaymentLink={handleGeneratePaymentLink}
            handleCopyPaymentLink={handleCopyPaymentLink}
            generateQRCode={generateQRCode}
            handleCheckInArtwork={handleCheckInArtwork}
            checkInLoading={checkInLoading}
            onSelectArtwork={setSelectedArtwork}
            onRemoveFromCollection={removeFromCollection}
            onAddArtwork={() => navigate(`/curator/artworks?addToCollection=${collection.collection_id}`)}
            acceptances={acceptances}
          />
        )}

        {activeTab === 'artists' && (
          <div className="artists-tab">
            
            {/* Controls: Search + Sort + Per Page */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.5rem 0 1rem' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  value={artistSearch}
                  onChange={(e) => {
                    setArtistSearch(e.target.value);
                    setArtistPage(1);
                  }}
                  placeholder="Search artists..."
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem 0.625rem 2.25rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div style={{ position: 'relative' }}>
                <select
                  value={artistSort}
                  onChange={(e) => {
                    setArtistSort(e.target.value);
                    setArtistPage(1);
                  }}
                  style={{
                    appearance: 'none',
                    padding: '0.625rem 2rem 0.625rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    backgroundColor: '#f3f4f6',
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value="name">Name</option>
                  <option value="newest">Newest</option>
                  <option value="accepted">Accepted</option>
                  <option value="not_accepted">Not Accepted</option>
                  <option value="artworks">Artworks</option>
                  <option value="paid">Paid</option>
                  <option value="needs_payment">Needs Payment</option>
                  <option value="needs_terms">Needs Terms</option>
                </select>
                <ArrowUpDown size={12} style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', pointerEvents: 'none' }} />
              </div>
              <select
                value={artistsPerPage}
                onChange={(e) => {
                  setArtistsPerPage(Number(e.target.value));
                  setArtistPage(1);
                }}
                style={{
                  padding: '0.625rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  backgroundColor: '#f3f4f6',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={48}>48</option>
                <option value={100}>100</option>
              </select>
            </div>

            {artists.length === 0 ? (
              <div className="empty-artists">
                <Users className="empty-icon" />
                <h3>No Artists</h3>
                <p>This collection doesn't have any participating artists yet.</p>
              </div>
            ) : (() => {
              // Filter and sort artists
              // Helper to check payment status
              const getArtistPaymentStatus = (artist) => {
                const acceptanceRecord = acceptances.find(acc => acc.user_id === artist.uuid);
                const hasPaid = acceptances.some(acc => 
                  acc.user_id === artist.uuid && 
                  acc.paid === true && 
                  acc.purchase_id !== null
                );
                const hasWaiver = acceptances.some(acc => 
                  acc.user_id === artist.uuid && 
                  acc.paid === true && 
                  acc.purchase_id === null
                );
                const hasGrantWaiver = acceptances.some(acc => 
                  acc.user_id === artist.uuid && 
                  acc.grant_waiver === true
                );
                const acceptedCount = artworks.filter(aw => aw.artist?.uuid === artist.uuid && aw.accepted).length;
                return { hasPaid, hasWaiver, hasGrantWaiver, acceptedCount, acceptanceRecord };
              };

              const filteredSortedArtists = artists
                .filter((artist) => {
                  if (!artistSearch.trim()) return true;
                  const s = artistSearch.toLowerCase();
                  const name = (artist.nickname || `${artist.first_name || ''} ${artist.last_name || ''}`).toLowerCase();
                  const email = (artist.email || '').toLowerCase();
                  return name.includes(s) || email.includes(s);
                })
                .sort((a, b) => {
                  if (artistSort === 'newest') {
                    return new Date(b.joined_collection || 0) - new Date(a.joined_collection || 0);
                  }
                  if (artistSort === 'accepted') {
                    const aCount = artworks.filter(aw => aw.artist?.uuid === a.uuid && aw.accepted).length;
                    const bCount = artworks.filter(aw => aw.artist?.uuid === b.uuid && aw.accepted).length;
                    return bCount - aCount;
                  }
                  if (artistSort === 'artworks') {
                    const aCount = artworks.filter(aw => aw.artist?.uuid === a.uuid).length;
                    const bCount = artworks.filter(aw => aw.artist?.uuid === b.uuid).length;
                    return bCount - aCount;
                  }
                  if (artistSort === 'paid') {
                    const aStatus = getArtistPaymentStatus(a);
                    const bStatus = getArtistPaymentStatus(b);
                    // Paid first, then waiver, then unpaid
                    const aScore = aStatus.hasPaid ? 2 : (aStatus.hasWaiver || aStatus.hasGrantWaiver ? 1 : 0);
                    const bScore = bStatus.hasPaid ? 2 : (bStatus.hasWaiver || bStatus.hasGrantWaiver ? 1 : 0);
                    return bScore - aScore;
                  }
                  if (artistSort === 'needs_payment') {
                    const aStatus = getArtistPaymentStatus(a);
                    const bStatus = getArtistPaymentStatus(b);
                    // Artists with accepted artworks but no payment/waiver come first
                    const aNeedsPayment = aStatus.acceptedCount > 0 && !aStatus.hasPaid && !aStatus.hasWaiver && !aStatus.hasGrantWaiver;
                    const bNeedsPayment = bStatus.acceptedCount > 0 && !bStatus.hasPaid && !bStatus.hasWaiver && !bStatus.hasGrantWaiver;
                    if (aNeedsPayment && !bNeedsPayment) return -1;
                    if (!aNeedsPayment && bNeedsPayment) return 1;
                    // Secondary sort by accepted count
                    return bStatus.acceptedCount - aStatus.acceptedCount;
                  }
                  if (artistSort === 'not_accepted') {
                    // Artists with 0 accepted artworks first, then sort by total artworks desc
                    const aAccepted = artworks.filter(aw => aw.artist?.uuid === a.uuid && aw.accepted).length;
                    const bAccepted = artworks.filter(aw => aw.artist?.uuid === b.uuid && aw.accepted).length;
                    if (aAccepted === 0 && bAccepted > 0) return -1;
                    if (aAccepted > 0 && bAccepted === 0) return 1;
                    // Secondary: sort by total artworks (more artworks = higher priority)
                    const aTotal = artworks.filter(aw => aw.artist?.uuid === a.uuid).length;
                    const bTotal = artworks.filter(aw => aw.artist?.uuid === b.uuid).length;
                    return bTotal - aTotal;
                  }
                  if (artistSort === 'needs_terms') {
                    // Artists who haven't accepted terms first
                    const aRecord = acceptances.find(acc => acc.user_id === a.uuid);
                    const bRecord = acceptances.find(acc => acc.user_id === b.uuid);
                    const aHasTerms = aRecord && aRecord.accepted_at;
                    const bHasTerms = bRecord && bRecord.accepted_at;
                    if (!aHasTerms && bHasTerms) return -1;
                    if (aHasTerms && !bHasTerms) return 1;
                    // Secondary: sort by accepted artwork count (more accepted = higher priority)
                    const aAccepted = artworks.filter(aw => aw.artist?.uuid === a.uuid && aw.accepted).length;
                    const bAccepted = artworks.filter(aw => aw.artist?.uuid === b.uuid && aw.accepted).length;
                    return bAccepted - aAccepted;
                  }
                  const an = (a.nickname || `${a.first_name || ''} ${a.last_name || ''}`).toLowerCase();
                  const bn = (b.nickname || `${b.first_name || ''} ${b.last_name || ''}`).toLowerCase();
                  return an.localeCompare(bn);
                });
              
              const totalPages = Math.ceil(filteredSortedArtists.length / artistsPerPage);
              const paginatedArtists = filteredSortedArtists.slice(
                (artistPage - 1) * artistsPerPage,
                artistPage * artistsPerPage
              );
              
              // Reset to page 1 if current page is beyond available pages
              if (artistPage > totalPages && totalPages > 0) {
                setArtistPage(1);
              }
              
              return (
                <>
                  {/* Results count */}
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                    Showing {((artistPage - 1) * artistsPerPage) + 1}-{Math.min(artistPage * artistsPerPage, filteredSortedArtists.length)} of {filteredSortedArtists.length} artists
                  </div>
                  
                  <div className="artists-masonry" style={{
                    padding: '0.5rem 0'
                  }}>
                    {paginatedArtists.map((artist) => {
                    // Get all artworks for this artist
                    const artistAllArtworks = artworks.filter(artwork => artwork.artist?.uuid === artist.uuid);
                    const acceptedCount = artistAllArtworks.filter(a => a.accepted).length;
                    const notAcceptedCount = artistAllArtworks.filter(a => !a.accepted).length;
                    
                    // Get acceptance/waiver status from artist_submission_mapping
                    const acceptanceRecord = acceptances.find(acc => acc.user_id === artist.uuid);
                    const hasAcceptedTerms = acceptanceRecord && acceptanceRecord.accepted_at;
                    // Has paid = paid is true AND has a purchase_id (actual payment)
                    const hasPaidActual = acceptances.some(acc => 
                      acc.user_id === artist.uuid && 
                      acc.paid === true && 
                      acc.purchase_id !== null
                    );
                    // Waiver = paid is true but NO purchase_id (fee was waived)
                    const hasWaiver = acceptances.some(acc => 
                      acc.user_id === artist.uuid && 
                      acc.purchase_id === null && 
                      acc.paid === true
                    );
                    
                    return (
                      <div 
                        key={artist.uuid} 
                        style={{
                          background: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '10px',
                          padding: '1rem',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          breakInside: 'avoid',
                          marginBottom: '1rem'
                        }}
                        onClick={() => navigate(`/curator/collections/${collectionId}/artists/${artist.uuid}`)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#3b82f6';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                        }}
                      >
                        {/* Header: Name + View Details */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0 }}>
                            {artist.nickname || `${artist.first_name} ${artist.last_name}`}
                          </h3>
                          <button
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              navigate(`/curator/collections/${collectionId}/artists/${artist.uuid}`);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              fontWeight: '500'
                            }}
                            title="View artist details"
                          >
                            <ExternalLink size={10} />
                            Details
                          </button>
                        </div>

                        {/* Contact Info Section */}
                        <div style={{ 
                          fontSize: '0.7rem', 
                          color: '#6b7280', 
                          marginBottom: '0.5rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.125rem'
                        }}>
                          {/* Full name if different from nickname */}
                          {artist.nickname && (artist.first_name || artist.last_name) && (
                            <div style={{ color: '#374151' }}>
                              {artist.first_name} {artist.last_name}
                            </div>
                          )}
                          {/* Handle */}
                          {artist.handle && (
                            <div style={{ color: '#9ca3af' }}>@{artist.handle}</div>
                          )}
                          {/* Email with copy button */}
                          {artist.email && (
                            <div 
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.25rem',
                                cursor: 'pointer'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(artist.email);
                              }}
                              title="Click to copy email"
                            >
                              <span style={{ fontSize: '0.65rem' }}>📧</span>
                              <span style={{ color: '#3b82f6', textDecoration: 'underline' }}>{artist.email}</span>
                            </div>
                          )}
                          {/* Mobile phone */}
                          {artist.mobile_phone && (
                            <div 
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.25rem',
                                cursor: 'pointer'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(artist.mobile_phone);
                              }}
                              title="Click to copy phone"
                            >
                              <span style={{ fontSize: '0.65rem' }}>📱</span>
                              <span style={{ color: '#3b82f6' }}>{artist.mobile_phone}</span>
                            </div>
                          )}
                        </div>

                        {/* Stats line with total value */}
                        {(() => {
                          const artistTotalValue = artistAllArtworks.reduce((sum, aw) => sum + (Number(aw.price) || 0), 0);
                          return (
                            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.75rem 0' }}>
                              <span style={{ fontWeight: '600', color: '#374151' }}>{artistAllArtworks.length}</span> artworks · <span style={{ fontWeight: '600', color: '#10b981' }}>{acceptedCount}</span> accepted · <span style={{ fontWeight: '600', color: '#15803d' }}>${artistTotalValue.toLocaleString()}</span>
                            </p>
                          );
                        })()}

                        {/* Compact status badges - all in one row */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.75rem' }}>
                          {artist.has_app && (
                            <span style={{ fontSize: '0.65rem', fontWeight: '500', padding: '0.2rem 0.4rem', borderRadius: '0.25rem', backgroundColor: '#dbeafe', color: '#1e40af' }}>
                              📱 App
                            </span>
                          )}
                          {/* Accepted into collection */}
                          {acceptedCount > 0 && (
                            <span style={{ fontSize: '0.65rem', fontWeight: '500', padding: '0.2rem 0.4rem', borderRadius: '0.25rem', backgroundColor: '#d1fae5', color: '#065f46' }}>
                              ✓ Accepted
                            </span>
                          )}
                          {/* Terms accepted */}
                          <span style={{ 
                            fontSize: '0.65rem', 
                            fontWeight: '500', 
                            padding: '0.2rem 0.4rem', 
                            borderRadius: '0.25rem', 
                            backgroundColor: hasAcceptedTerms ? '#d1fae5' : '#f3f4f6', 
                            color: hasAcceptedTerms ? '#065f46' : '#9ca3af',
                            cursor: acceptanceRecord?.acceptance_uuid ? 'pointer' : 'default'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (acceptanceRecord?.acceptance_uuid) {
                              const url = `${window.location.origin}/acceptance/${acceptanceRecord.acceptance_uuid}`;
                              navigator.clipboard.writeText(url);
                            }
                          }}
                          title={acceptanceRecord?.acceptance_uuid ? 'Click to copy acceptance URL' : 'Terms not yet accepted'}
                          >
                            {hasAcceptedTerms ? '✓ Terms' : '○ Terms'}
                          </span>
                          {/* Paid - only show if they have actual purchase_id (not waiver) */}
                          {hasPaidActual && (
                            <span style={{ fontSize: '0.65rem', fontWeight: '500', padding: '0.2rem 0.4rem', borderRadius: '0.25rem', backgroundColor: '#dbeafe', color: '#1e40af' }}>
                              ✓ Paid
                            </span>
                          )}
                          {/* Waiver - only show if waiver was issued (paid=true but no purchase_id) */}
                          {hasWaiver && (
                            <span style={{ fontSize: '0.65rem', fontWeight: '500', padding: '0.2rem 0.4rem', borderRadius: '0.25rem', backgroundColor: '#fef3c7', color: '#92400e' }}>
                              ⚡ Waiver
                            </span>
                          )}
                          {/* Grant Waiver button - show if they have accepted artwork but haven't paid and no waiver */}
                          {acceptedCount > 0 && !hasPaidActual && !hasWaiver && (
                            <span 
                              style={{ 
                                fontSize: '0.65rem', 
                                fontWeight: '500', 
                                padding: '0.2rem 0.4rem', 
                                borderRadius: '0.25rem', 
                                backgroundColor: '#fef3c7', 
                                color: '#92400e',
                                cursor: 'pointer',
                                border: '1px dashed #d97706'
                              }}
                              onClick={(e) => { e.stopPropagation(); handleGrantWaiver(artist); }}
                              title="Click to grant fee waiver"
                            >
                              + Grant Waiver
                            </span>
                          )}
                        </div>

                        {/* Artwork List - Compact view with accepted on top */}
                        {artistAllArtworks.length > 0 && (() => {
                          // Sort: accepted first, then by title
                          const sortedArtworks = [...artistAllArtworks].sort((a, b) => {
                            if (a.accepted && !b.accepted) return -1;
                            if (!a.accepted && b.accepted) return 1;
                            return (a.title || 'Untitled').localeCompare(b.title || 'Untitled');
                          });
                          
                          return (
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.25rem'
                            }}>
                              {sortedArtworks.map((artwork) => (
                                <div
                                  key={artwork.vc_id}
                                  onClick={(e) => { e.stopPropagation(); setSelectedArtwork(artwork); }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.375rem',
                                    borderRadius: '4px',
                                    backgroundColor: artwork.accepted ? '#ecfdf5' : '#f9fafb',
                                    border: `1px solid ${artwork.accepted ? '#10b981' : '#e5e7eb'}`,
                                    cursor: 'pointer',
                                    transition: 'background-color 0.15s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = artwork.accepted ? '#d1fae5' : '#f3f4f6';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = artwork.accepted ? '#ecfdf5' : '#f9fafb';
                                  }}
                                >
                                  {/* Small thumbnail */}
                                  <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '3px',
                                    overflow: 'hidden',
                                    flexShrink: 0,
                                    backgroundColor: '#f3f4f6'
                                  }}>
                                    {artwork.cover_image ? (
                                      <img
                                        src={addCloudinaryTransform(artwork.cover_image, 't_square-150/')}
                                        alt={artwork.title}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      />
                                    ) : (
                                      <div style={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}>
                                        <ImageIcon size={12} color="#9ca3af" />
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Title and price */}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                      fontSize: '0.75rem',
                                      fontWeight: '500',
                                      color: '#374151',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}>
                                      {artwork.title || 'Untitled'}
                                    </div>
                                    <div style={{
                                      fontSize: '0.65rem',
                                      color: '#6b7280'
                                    }}>
                                      {artwork.price ? `$${Number(artwork.price).toLocaleString()}` : 'NFS'}
                                      {judgingTotalsByVcId[artwork.vc_id] != null && (
                                        <span style={{ color: '#4338ca', fontWeight: '600' }}>
                                          {' '}· Score: {judgingTotalsByVcId[artwork.vc_id]}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Status indicator */}
                                  <div style={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    backgroundColor: artwork.accepted ? '#10b981' : '#f59e0b',
                                    color: 'white',
                                    fontSize: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: '700',
                                    flexShrink: 0
                                  }}>
                                    {artwork.accepted ? '✓' : '○'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                  </div>
                  
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginTop: '1.5rem',
                      paddingTop: '1rem',
                      borderTop: '1px solid #e5e7eb',
                      flexWrap: 'wrap'
                    }}>
                      {/* First & Previous */}
                      <button
                        onClick={() => setArtistPage(1)}
                        disabled={artistPage === 1}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem',
                          backgroundColor: artistPage === 1 ? '#f3f4f6' : 'white',
                          color: artistPage === 1 ? '#9ca3af' : '#374151',
                          cursor: artistPage === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}
                        title="First page"
                      >
                        ««
                      </button>
                      <button
                        onClick={() => setArtistPage(p => Math.max(1, p - 1))}
                        disabled={artistPage === 1}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem',
                          backgroundColor: artistPage === 1 ? '#f3f4f6' : 'white',
                          color: artistPage === 1 ? '#9ca3af' : '#374151',
                          cursor: artistPage === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}
                      >
                        «
                      </button>
                      
                      {/* Page numbers with ellipsis */}
                      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                        {(() => {
                          const pages = [];
                          const showEllipsisStart = artistPage > 4;
                          const showEllipsisEnd = artistPage < totalPages - 3;
                          
                          // Always show page 1
                          pages.push(1);
                          
                          // Start ellipsis
                          if (showEllipsisStart) {
                            pages.push('...');
                          }
                          
                          // Pages around current
                          for (let i = Math.max(2, artistPage - 2); i <= Math.min(totalPages - 1, artistPage + 2); i++) {
                            if (!pages.includes(i)) {
                              pages.push(i);
                            }
                          }
                          
                          // End ellipsis
                          if (showEllipsisEnd) {
                            pages.push('...');
                          }
                          
                          // Always show last page
                          if (totalPages > 1 && !pages.includes(totalPages)) {
                            pages.push(totalPages);
                          }
                          
                          return pages.map((page, idx) => (
                            page === '...' ? (
                              <span key={`ellipsis-${idx}`} style={{ padding: '0 0.5rem', color: '#9ca3af' }}>...</span>
                            ) : (
                              <button
                                key={page}
                                onClick={() => setArtistPage(page)}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  border: '1px solid',
                                  borderColor: artistPage === page ? '#8b5cf6' : '#d1d5db',
                                  borderRadius: '0.375rem',
                                  backgroundColor: artistPage === page ? '#8b5cf6' : 'white',
                                  color: artistPage === page ? 'white' : '#374151',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  fontWeight: artistPage === page ? '600' : '400',
                                  minWidth: '2.5rem'
                                }}
                              >
                                {page}
                              </button>
                            )
                          ));
                        })()}
                      </div>
                      
                      {/* Next & Last */}
                      <button
                        onClick={() => setArtistPage(p => Math.min(totalPages, p + 1))}
                        disabled={artistPage === totalPages}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem',
                          backgroundColor: artistPage === totalPages ? '#f3f4f6' : 'white',
                          color: artistPage === totalPages ? '#9ca3af' : '#374151',
                          cursor: artistPage === totalPages ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}
                      >
                        »
                      </button>
                      <button
                        onClick={() => setArtistPage(totalPages)}
                        disabled={artistPage === totalPages}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem',
                          backgroundColor: artistPage === totalPages ? '#f3f4f6' : 'white',
                          color: artistPage === totalPages ? '#9ca3af' : '#374151',
                          cursor: artistPage === totalPages ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}
                        title="Last page"
                      >
                        »»
                      </button>
                      
                      {/* Jump to page */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1rem' }}>
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Go to:</span>
                        <input
                          type="number"
                          min={1}
                          max={totalPages}
                          value={artistPage}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            if (val >= 1 && val <= totalPages) {
                              setArtistPage(val);
                            }
                          }}
                          style={{
                            width: '4rem',
                            padding: '0.375rem 0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            textAlign: 'center'
                          }}
                        />
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>of {totalPages}</span>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {activeTab === 'communications' && (
          <CommunicationPanel
            collection={collection}
            artworks={artworks}
            artists={artists}
            acceptances={acceptances}
          />
        )}

        {/* Events Tab - Shows all events linked to this collection */}
        {activeTab === 'events' && (
          <div className="events-list-tab">
            <div className="events-list-header">
              <h3>Events for This Collection</h3>
              <p className="events-list-subtitle">
                Manage multiple events associated with this exhibition
              </p>
              <button 
                className="add-event-btn"
                onClick={() => navigate(`/curator/events/new?collection_id=${collectionId}`)}
              >
                <Calendar size={16} />
                Add Event
              </button>
            </div>
            
            {collectionEvents.length === 0 ? (
              <div className="events-empty-state">
                <Calendar size={48} color="#9ca3af" />
                <p>No events linked to this collection yet.</p>
                <button 
                  className="add-event-btn primary"
                  onClick={() => navigate(`/curator/events/new?collection_id=${collectionId}`)}
                >
                  <Calendar size={16} />
                  Create First Event
                </button>
              </div>
            ) : (
              <div className="events-list-grid">
                {collectionEvents.map((event) => {
                  const eventDate = new Date(event.event_start);
                  const isPast = eventDate < new Date();
                  const isUpcoming = !isPast && eventDate - new Date() < 7 * 24 * 60 * 60 * 1000; // Within 7 days
                  
                  return (
                    <div 
                      key={event.id} 
                      className={`event-card-item compact ${isPast ? 'past' : ''} ${isUpcoming ? 'upcoming' : ''}`}
                      onClick={() => navigate(`/curator/events/${event.id}`)}
                    >
                      <div className="event-card-content">
                        <div className="event-card-title-row">
                          <h4>{event.name}</h4>
                          <span className={`event-status-badge inline ${event.status || 'draft'}`}>
                            {event.status || 'draft'}
                          </span>
                        </div>
                        <div className="event-card-date">
                          <Calendar size={14} />
                          {eventDate.toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                          {' at '}
                          {eventDate.toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit'
                          })}
                        </div>
                        {event.galleries?.name && (
                          <div className="event-card-venue">
                            <MapPin size={14} />
                            {event.galleries.name}
                          </div>
                        )}
                        {event.ticket_sales_enabled && (
                          <div className="event-card-tickets">
                            <Ticket size={14} />
                            <strong>{event.tickets_sold || 0}</strong> / {event.tickets_available || '∞'} tickets sold
                          </div>
                        )}
                        {event.tickets_sold > 0 && (
                          <div className="event-card-revenue">
                            <DollarSign size={14} />
                            ${event.total_revenue?.toFixed(2) || '0.00'} revenue
                          </div>
                        )}
                      </div>
                      <div className="event-card-actions">
                        <button 
                          className="event-action-btn share"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`https://share.vibrantcast.com/event?id=${event.id}`, '_blank');
                          }}
                          title="Ticket Sales Page"
                        >
                          <Share2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                
                {/* Add Event Card */}
                <div 
                  className="event-card-item add-new"
                  onClick={() => navigate(`/curator/events/new?collection_id=${collectionId}`)}
                >
                  <div className="add-event-placeholder">
                    <Calendar size={32} />
                    <span>Add Another Event</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'submissions' && (
          <SubmissionTab
            collection={collection}
            eventDetails={eventDetails}
            submissionDetails={submissionDetails}
            loading={false}
            onCreateSubmission={() => navigate(`/curator/submissions/new?collection_id=${collection.collection_id}`)}
            onEditSubmission={() => window.open(`/curator/collections/${collection.collection_id}/edit-submission`, '_blank')}
            onCreateEvent={() => navigate(`/curator/collections/${collection.collection_id}?tab=event`)}
            onAddArtwork={() => navigate(`/curator/artworks?addToCollection=${collection.collection_id}`)}
          />
        )}

        {activeTab === 'inquiries' && (
          <div className="inquiries-tab">
            <div className="tab-header">
              <h3>Collection Activity</h3>
              <div className="filters">
                <select 
                  value={inquiryFilter} 
                  onChange={(e) => setInquiryFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Notifications</option>
                  <option value="collector_inquiry">Collector Inquiries</option>
                  <option value="other">Other Notifications</option>
                </select>
                <select 
                  value={inquirySort} 
                  onChange={(e) => setInquirySort(e.target.value)}
                  className="sort-select"
                >
                  <option value="date">Sort by Date</option>
                  <option value="type">Sort by Type</option>
                  <option value="priority">Sort by Priority</option>
                </select>
              </div>
            </div>

            {inquiriesLoading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading inquiries...</p>
              </div>
            ) : inquiriesError ? (
              <div className="error-state">
                <AlertCircle size={48} />
                <p>Error loading inquiries: {inquiriesError}</p>
                <button onClick={loadCollectorInquiries} className="retry-button">
                  <RefreshCw size={16} />
                  Retry
                </button>
              </div>
            ) : collectorInquiries.length === 0 ? (
              <div className="empty-state">
                <Bell size={48} />
                <h3>No Collection Activity</h3>
                <p>No notifications or collector inquiries yet for this collection.</p>
              </div>
            ) : (
              <div className="inquiries-list">
                {collectorInquiries
                  .filter(inquiry => {
                    if (inquiryFilter === 'collector_inquiry') {
                      return inquiry.category === 'collector_inquiry';
                    } else if (inquiryFilter === 'other') {
                      return inquiry.category !== 'collector_inquiry';
                    }
                    return true;
                  })
                  .sort((a, b) => {
                    if (inquirySort === 'priority') {
                      return a.priority - b.priority;
                    } else if (inquirySort === 'type') {
                      return a.category?.localeCompare(b.category);
                    }
                    return new Date(b.created_at) - new Date(a.created_at);
                  })
                  .map(inquiry => (
                    <div 
                      key={inquiry.id} 
                      className={`inquiry-card ${inquiry.category === 'collector_inquiry' ? 'priority' : ''} ${inquiry.read ? 'read' : 'unread'}`}
                    >
                      <div className="inquiry-header">
                        <div className="inquiry-type">
                          <Bell size={16} />
                          <span className="category-badge">{inquiry.category || inquiry.type}</span>
                          {inquiry.category === 'collector_inquiry' && (
                            <span className="priority-badge">High Priority</span>
                          )}
                        </div>
                        <div className="inquiry-date">
                          {inquiry.formatted_date}
                        </div>
                      </div>
                      
                      <div className="inquiry-content">
                        <h4>{inquiry.title}</h4>
                        <p>{inquiry.body}</p>
                        
                        {inquiry.artwork && (
                          <div className="artwork-info">
                            <strong>Artwork:</strong> {inquiry.artwork.title} by {inquiry.artwork.artist?.nickname || 
                              `${inquiry.artwork.artist?.first_name} ${inquiry.artwork.artist?.last_name}`}
                          </div>
                        )}
                        
                        {inquiry.metadata && inquiry.category === 'collector_inquiry' && (
                          <div className="collector-details">
                            <h5>Collector Information:</h5>
                            <div className="collector-info-grid">
                              {inquiry.metadata.collector_name && (
                                <div><strong>Name:</strong> {inquiry.metadata.collector_name}</div>
                              )}
                              {inquiry.metadata.collector_email && (
                                <div><strong>Email:</strong> 
                                  <a href={`mailto:${inquiry.metadata.collector_email}`}>
                                    {inquiry.metadata.collector_email}
                                  </a>
                                </div>
                              )}
                              {inquiry.metadata.collector_phone && (
                                <div><strong>Phone:</strong> 
                                  <a href={`tel:${inquiry.metadata.collector_phone}`}>
                                    {inquiry.metadata.collector_phone}
                                  </a>
                                </div>
                              )}
                              {inquiry.metadata.message && (
                                <div><strong>Inquiry:</strong> {inquiry.metadata.message}</div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="user-info">
                          {inquiry.sender_user && (
                            <div><strong>From:</strong> {inquiry.sender_user.nickname || 
                              `${inquiry.sender_user.first_name} ${inquiry.sender_user.last_name}`}
                              {inquiry.sender_user.email && (
                                <span> ({inquiry.sender_user.email})</span>
                              )}
                            </div>
                          )}
                          {inquiry.recipient_user && (
                            <div><strong>To:</strong> {inquiry.recipient_user.nickname || 
                              `${inquiry.recipient_user.first_name} ${inquiry.recipient_user.last_name}`}
                              {inquiry.recipient_user.email && (
                                <span> ({inquiry.recipient_user.email})</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="inquiry-actions">
                        {!inquiry.read && (
                          <button 
                            className="mark-read-button"
                            onClick={async () => {
                              try {
                                await supabase
                                  .from('notifications')
                                  .update({ read: true })
                                  .eq('id', inquiry.id);
                                
                                // Update local state
                                setCollectorInquiries(prev => 
                                  prev.map(inq => 
                                    inq.id === inquiry.id 
                                      ? { ...inq, read: true }
                                      : inq
                                  )
                                );
                              } catch (err) {
                                console.error('Error marking as read:', err);
                              }
                            }}
                          >
                            <Check size={16} />
                            Mark as Read
                          </button>
                        )}
                        
                        {inquiry.category === 'collector_inquiry' && inquiry.metadata?.collector_email && (
                          <button 
                            className="respond-button"
                            onClick={() => {
                              const subject = `Re: Inquiry about ${inquiry.artwork?.title || 'Artwork'}`;
                              const body = `Hello ${inquiry.metadata.collector_name || 'there'},\n\nThank you for your inquiry about "${inquiry.artwork?.title || 'the artwork'}". `;
                              window.open(`mailto:${inquiry.metadata.collector_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
                            }}
                          >
                            <Mail size={16} />
                            Respond
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'acceptances' && (
          <div className="acceptances-tab">
            <AcceptancesPanel 
              artworks={artworks}
              toggleAcceptanceStatus={toggleAcceptanceStatus}
              onSelectArtwork={setSelectedArtwork}
            />
          </div>
        )}

        {/* Note: Prints tab navigates to /curator/collections/:id/prints standalone page */}

        {activeTab === 'voting' && (
          <VotingAdminSummary collectionId={collection.collection_id} setSelectedArtwork={setSelectedArtwork} />
        )}

        {activeTab === 'judging' && (
          <JudgingTab collectionId={collection.collection_id} />
        )}

        {activeTab === 'financials' && (
          <FinancialSummaryTab
            collectionId={collection.collection_id}
            collectionEvents={collectionEvents}
            submissionDetails={submissionDetails}
          />
        )}

        {activeTab === 'checkin' && (
          <div style={{ padding: '12px' }}>
            {/* Mobile-optimized Check-in Controls */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '10px',
              border: '1px solid #e5e7eb'
            }}>
              {/* Filter buttons row */}
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => setCheckInFilter('all')}
                  style={{
                    flex: 1,
                    padding: '10px 8px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    backgroundColor: checkInFilter === 'all' ? '#8b5cf6' : 'white',
                    color: checkInFilter === 'all' ? 'white' : '#374151',
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  All ({artworks.filter(art => art.accepted === true).length})
                </button>
                <button
                  onClick={() => setCheckInFilter('checked-in')}
                  style={{
                    flex: 1,
                    padding: '10px 8px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    backgroundColor: checkInFilter === 'checked-in' ? '#16a34a' : 'white',
                    color: checkInFilter === 'checked-in' ? 'white' : '#374151',
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  ✓ ({artworks.filter(art => art.accepted === true && art.location === eventDetails?.gallery_id).length})
                </button>
                <button
                  onClick={() => setCheckInFilter('pending')}
                  style={{
                    flex: 1,
                    padding: '10px 8px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    backgroundColor: checkInFilter === 'pending' ? '#d97706' : 'white',
                    color: checkInFilter === 'pending' ? 'white' : '#374151',
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  ○ ({artworks.filter(art => art.accepted === true && art.location !== eventDetails?.gallery_id).length})
                </button>
              </div>
              
              {/* Search */}
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search..."
                  value={checkInSearch}
                  onChange={(e) => setCheckInSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 36px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                />
                <svg 
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}
                  width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </div>
              
              {/* Check In All / Check Out All */}
              {(() => {
                const pendingCount = artworks.filter(art => art.accepted === true && art.location !== eventDetails?.gallery_id).length;
                const checkedInCount = artworks.filter(art => art.accepted === true && art.location === eventDetails?.gallery_id).length;
                return (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={(e) => { e.preventDefault(); handleBulkCheckInAll(); }}
                      disabled={checkInLoading || pendingCount === 0}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: pendingCount === 0 ? '#9ca3af' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        cursor: pendingCount === 0 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      <CheckCircle size={14} />
                      {checkInLoading ? 'Working...' : `Check In All (${pendingCount})`}
                    </button>
                    <button
                      onClick={(e) => { e.preventDefault(); handleBulkCheckOutAll(); }}
                      disabled={checkInLoading || checkedInCount === 0}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: checkedInCount === 0 ? '#9ca3af' : '#f97316',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        cursor: checkedInCount === 0 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      <X size={14} />
                      {checkInLoading ? 'Working...' : `Check Out All (${checkedInCount})`}
                    </button>
                  </div>
                );
              })()}
            </div>

            {/* Artworks List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {displayedCheckInArtworks.map((artwork, index) => (
                <CompactArtworkCard
                  key={`artwork-${artwork.vc_id}-${index}`}
                  artwork={artwork}
                  eventDetails={eventDetails}
                  checkInLoading={checkInLoading}
                  onCheckIn={handleCheckInArtwork}
                  onSelectArtwork={setSelectedArtwork}
                />
              ))}
            </div>
            
            {/* Load More / Infinite scroll */}
            {hasMoreCheckIn && (
              <div
                ref={checkInLoaderRef}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '20px',
                  gap: '12px'
                }}
              >
                {checkInLoadingMore ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '24px', height: '24px', border: '3px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading more...</span>
                  </div>
                ) : (
                  <button
                    onClick={loadMoreCheckIn}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'white',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Load More ({allCheckInArtworks.length - displayedCheckInArtworks.length} remaining)
                  </button>
                )}
              </div>
            )}
            
            {/* Show count indicator */}
            {allCheckInArtworks.length > 0 && (
              <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.75rem', marginTop: '8px', paddingBottom: '16px' }}>
                Showing {displayedCheckInArtworks.length} of {allCheckInArtworks.length} artworks
              </div>
            )}
            
            {allCheckInArtworks.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>
                <p>No artworks found matching your criteria.</p>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Purchase Panel */}
      <PurchasePanel
        purchasePanel={purchasePanel}
        isProcessingPayment={isProcessingPayment}
        copiedPaymentLink={copiedPaymentLink}
        qrCodeUrl={qrCodeUrl}
        paymentResult={paymentResult}
        onClose={() => setPurchasePanel(null)}
        onDirectPayment={handleDirectPayment}
        onGeneratePaymentLink={handleGeneratePaymentLink}
        onCopyPaymentLink={handleCopyPaymentLink}
        onSwitchMode={(mode) => setPurchasePanel({ ...purchasePanel, mode })}
        onCompletePayment={() => {
          setPurchasePanel(null);
          setPaymentResult(null);
        }}
      />

      {/* Artwork Detail Modal */}
      {selectedArtwork && (
        <div className="artwork-modal" onClick={() => setSelectedArtwork(null)}>
          <div className="artwork-modal-content" onClick={e => e.stopPropagation()}>
            <div className="artwork-modal-header">
              <h3>{selectedArtwork.title}</h3>
              <button onClick={() => setSelectedArtwork(null)}>×</button>
            </div>
            <div className="artwork-modal-body">
              <div className="artwork-modal-image">
                <img 
                  src={selectedArtwork.cover_image?.includes('cloudinary.com') 
                    ? selectedArtwork.cover_image.replace('/upload/', '/upload/t_800/') 
                    : selectedArtwork.cover_image} 
                  alt={selectedArtwork.title} 
                />
              </div>
              <div className="artwork-modal-details">
                <p><strong>Artist:</strong> {
                  selectedArtwork.artist ? 
                    (selectedArtwork.artist.nickname || `${selectedArtwork.artist.first_name} ${selectedArtwork.artist.last_name}`) :
                    selectedArtwork.artist_name || 'Unknown Artist'
                }</p>
                {selectedArtwork.status && <p><strong>Status:</strong> {selectedArtwork.status}</p>}
                {selectedArtwork.width && selectedArtwork.height && (
                  <p><strong>Dimensions:</strong> {selectedArtwork.width}"W × {selectedArtwork.height}"H{selectedArtwork.depth ? ` × ${selectedArtwork.depth}"D` : ''}</p>
                )}
                {selectedArtwork.price && <p><strong>Price:</strong> ${selectedArtwork.price.toLocaleString()}{selectedArtwork.sold && <span style={{ marginLeft: '8px', background: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>SOLD</span>}</p>}

                {/* Action Buttons - Above description */}
                <div className="artwork-modal-actions">
                  {/* Submission Actions */}
                  <div className="action-group">
                    <span className="action-group-label">Submission</span>
                    <div className="action-group-buttons">
                      <button
                        onClick={() => {
                          toggleAcceptanceStatus(selectedArtwork);
                          setSelectedArtwork(prev => ({ ...prev, accepted: !prev.accepted }));
                        }}
                        className={`modal-action-btn ${selectedArtwork.accepted ? 'accepted' : 'pending'}`}
                      >
                        {selectedArtwork.accepted ? '✓ Accepted' : '○ Accept'}
                      </button>

                      {eventDetails && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            const scrollY = window.scrollY;
                            if (selectedArtwork.location === eventDetails.gallery_id) {
                              handleUnCheckInArtwork(selectedArtwork);
                              setSelectedArtwork(prev => ({ ...prev, location: null }));
                            } else {
                              handleCheckInArtwork(selectedArtwork);
                              setSelectedArtwork(prev => ({ ...prev, location: eventDetails.gallery_id }));
                            }
                            requestAnimationFrame(() => window.scrollTo(0, scrollY));
                          }}
                          disabled={checkInLoading}
                          className={`modal-action-btn ${selectedArtwork.location === eventDetails.gallery_id ? 'checked-in' : 'checkin'}`}
                        >
                          {selectedArtwork.location === eventDetails.gallery_id ? '↩ Check Out' : '📍 Check In'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Sales Actions - only if has price and not sold */}
                  {selectedArtwork.price && !selectedArtwork.sold && (
                    <div className="action-group">
                      <span className="action-group-label">Sales</span>
                      <div className="action-group-buttons">
                        {selectedArtwork.payment_link ? (
                          <>
                            <button
                              onClick={() => handleCopyPaymentLink(selectedArtwork.payment_link)}
                              className="modal-action-btn secondary"
                              title="Copy payment link"
                            >
                              {copiedPaymentLinks.has(selectedArtwork.payment_link) ? <Check size={14} /> : <Copy size={14} />}
                              {copiedPaymentLinks.has(selectedArtwork.payment_link) ? 'Copied!' : 'Copy Link'}
                            </button>
                            <button
                              onClick={() => window.open(selectedArtwork.payment_link, '_blank')}
                              className="modal-action-btn secondary"
                              title="Open payment link"
                            >
                              <ExternalLinkIcon size={14} />
                              Open
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleGeneratePaymentLink(selectedArtwork)}
                            disabled={isProcessingPayment || generatedPaymentLinks.has(selectedArtwork.vc_id)}
                            className="modal-action-btn payment"
                          >
                            <CreditCard size={14} />
                            {generatedPaymentLinks.has(selectedArtwork.vc_id) ? 'Link Generated' : isProcessingPayment ? 'Generating...' : 'Generate Payment Link'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Content Actions */}
                  <div className="action-group">
                    <span className="action-group-label">Content</span>
                    <div className="action-group-buttons">
                      <button
                        onClick={() => navigate(`/curator/artworks/${selectedArtwork.vc_id}/edit`)}
                        className="modal-action-btn edit"
                      >
                        <Edit size={14} />
                        Edit Artwork
                      </button>
                      <button
                        onClick={() => navigate(`/stories/create?artworkId=${selectedArtwork.vc_id}`)}
                        className="modal-action-btn story"
                      >
                        <BookOpen size={14} />
                        Create Story
                      </button>
                      {selectedArtwork.cover_image && (
                        <button
                          onClick={() => {
                            // Get original Cloudinary URL without transforms for printing
                            let downloadUrl = selectedArtwork.cover_image;
                            if (downloadUrl.includes('cloudinary.com')) {
                              // Remove any existing transforms and add download flag
                              downloadUrl = downloadUrl.replace(/\/upload\/[^/]+\//, '/upload/fl_attachment/');
                            }
                            window.open(downloadUrl, '_blank');
                          }}
                          className="modal-action-btn secondary"
                          title="Download original image for printing"
                        >
                          <Download size={14} />
                          Download
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Danger Zone */}
                  <div className="action-group danger">
                    <div className="action-group-buttons">
                      <button
                        onClick={() => {
                          if (window.confirm(`Remove "${selectedArtwork.title}" from this collection?`)) {
                            removeFromCollection(selectedArtwork);
                            setSelectedArtwork(null);
                          }
                        }}
                        className="modal-action-btn remove"
                      >
                        <X size={14} />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>

                {/* Description and additional details below actions */}
                {selectedArtwork.description && <p><strong>Description:</strong> {selectedArtwork.description}</p>}
                {selectedArtwork.more?.process && (
                  <p><strong>Process:</strong> {selectedArtwork.more.process}</p>
                )}
                {selectedArtwork.more?.inspiration && (
                  <p><strong>Inspiration:</strong> {selectedArtwork.more.inspiration}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Story Creation Modal */}
      {showQuickStoryModal && (
        <StoryCreationModal
          eventId={eventDetails?.id}
          onClose={() => setShowQuickStoryModal(false)}
          onSuccess={() => {
            setShowQuickStoryModal(false);
            // Refresh data after story creation
            window.location.reload(); // Simple refresh - could be optimized later
          }}
        />
      )}

      {/* Snackbar for notifications */}
      {snackbar && (
        <div 
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: snackbar.type === 'success' ? '#10b981' : '#ef4444',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 1000,
            maxWidth: '400px',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {snackbar.type === 'success' ? (
              <CheckCircle size={16} />
            ) : (
              <AlertCircle size={16} />
            )}
            {snackbar.message}
            <button
              onClick={() => setSnackbar(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '0',
                marginLeft: '8px'
              }}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
      </div>
  );
};
export default CollectionDetailAdmin;